// src/render/scenes/GameScene.ts — the Gaffer-On-Games accumulator that
// drives the sim at a fixed 30 Hz while the render loop runs at browser cadence.
//
// FOUND-02 load-bearing invariant: `tick()` ALWAYS receives a constant
// dt = TICK_SECONDS (1/30). The variable `delta` (Phaser's real frame ms)
// stays in the accumulator. Passing `delta` into `tick()` would break the
// golden-hash determinism guard — DO NOT regress.
//
// Phase 1 additions (MAP-01..13, MOVE-02..03):
//   - Tilemap preloaded by BootScene; GameScene.create() builds layers,
//     extracts MapData, pushes to sim via setMapData().
//   - Arcade Physics collider between CharacterSprite and the collision layer.
//   - Camera: startFollow + lerp + deadzone + lookahead offset (RESEARCH Pattern 6).
//   - A* smoke test on boot (MAP-13): findTilePath pool → threshing-floor.
//   - Controller swapped to the shared LocalInputController singleton
//     (Plan 04 Joystick pushes intents; replaces P0 placeholder controller).
//
// Phase 2 additions (VIS-02, VIS-05, VIS-06 — Plan 02-03):
//   - LightMask instantiated in create() at depth 15 (between DEPTH_WALL=10
//     and DEPTH_CHARACTER=20). setScrollFactor(1) so it follows the camera.
//   - update() forwards sim events (entity-moved, torch-flicked,
//     motion-light-on/off) from tick().events[] into renderBus so DevOverlay
//     and future P6 audio can subscribe without reading sim state directly.
//   - update() emits a synthetic `lighting-debug` snapshot ONCE per sim tick
//     carrying { torchOn, fovTileCount, motionLights[], tick } so DevOverlay
//     can render the locked Torch + FOV + Lights rows (UI-SPEC §DevOverlay P2).
//   - drawLightMask() runs each render frame; LightMask.redraw() skips stamps
//     when params.tick is unchanged (Pitfall 5 guardrail).
//
// P4 UAT Test 2 fix (hunters-frozen-at-spawn debug):
//   - initAiController for hunter-0/hider-1/hider-2 at the end of create() so
//     tick.ts Stage 16 (getAiController gate) has a real AIController to drive
//     decisions. Without this, hunters are frozen at spawn and UAT Test 2
//     (hunter telegraph escalation + convergence) cannot be exercised.
//   - update() builds an IntentBundle per AI-controlled entity by reading each
//     controller's getCachedIntent() (populated by the previous tick's Stage 16
//     decide call). Introduces a one-tick (~33 ms) lag between AI decision and
//     movement; imperceptible at human scales and preferred over duplicating
//     the full buildPerception + decide path on the render side.
//   - hunterSpawn prefers the `threshing-floor` zone (outdoor, far from the
//     hider plinth) rather than the plinth itself. Plinth is the HIDER base —
//     spawning the hunter on top of the player was semantically wrong and
//     visually confusing.
//   - NPC spawn positions (hunter + both NPC hiders) are snapped to the nearest
//     walkable tile so they never land inside a building interior on the real
//     authored map.
//
// Source: https://gafferongames.com/post/fix_your_timestep/
//         Phase 1 RESEARCH.md Pattern 1, Pattern 2, Pattern 6.
//         Phase 2 RESEARCH.md Pattern 2 (RenderTexture + blend modes),
//                       Pitfall 5 (skip-redraw-on-unchanged-tick).
//         .planning/debug/hunters-frozen-at-spawn.md (UAT Test 2 blocker).

// Phaser 4 ESM has NO default export.
import * as Phaser from 'phaser';
import Alea from 'alea';

import {
  localInputController,
  getAiController,
  initAiController,
  clearAiControllers,
} from '../../sim/controllers/registry';
import {
  DEFAULT_CHARACTER_ID,
  getProfile,
  hasProfile,
  listProfileIds,
} from '../../sim/characters/profiles';
import { setMapData } from '../../sim/map/data';
import { isWalkable } from '../../sim/map/grid';
import { findTilePath } from '../../sim/map/pathfinding';
import { zoneCenter } from '../../sim/map/zones';
import {
  P2_MOTION_LIGHT_STAY_TICKS,
  P2_MOTION_LIGHT_FADE_TICKS,
} from '../../sim/lighting/constants';
import { composeInitialRoster } from '../../sim/round/composition';
import { computeSpawnLayout, makeSpawnRng } from '../../sim/round/spawn-placement';
import { createRng } from '../../sim/rng';
import { tick, type IntentBundle } from '../../sim/tick';
import { isPositionBlocked, P1_CHARACTER_RADIUS } from '../../sim/movement';
import { createInitialWorldState } from '../../sim/world';
import type { GameEvent } from '../../shared/types/events';
import type { MapData, SurfacePolygon, RoofPolygon, TreeObject, ObstaclePolygon, FencePolyline } from '../../shared/types/map';
import type { Perception } from '../../shared/types/perception';
import type { Vec2 } from '../../shared/types/intent';
import type { WorldState, LightsState } from '../../shared/types/world';

import { isDebugEnabled } from '../../debug-flag';
import { renderBus, type MotionLightDebugEntry } from '../bus';
import { EventBus } from '../EventBus';
import { lerp } from '../interpolation';
import { LightMask, DARKNESS_FILL_HEX, type LightInstance, type TorchParams } from '../lighting/mask';
import { nextFadeState, type FadeGroup } from '../map/canopy-fade';
import {
  generateBlobVertices,
  isInsideEllipse,
  pointInPolygon,
  CANOPY_COLOR,
  CANOPY_FADE_ALPHA,
  CANOPY_MIN_POINTS,
  CANOPY_MAX_POINTS,
  type PolygonCanopyGroup,
  type PolygonRoofGroup,
} from '../map/polygon-canopy';
import { extractMapData } from '../map/tilemap-loader';
import { CharacterSprite } from '../sprites/character-sprite';

const TICK_MS = 1000 / 30;
const TICK_SECONDS = 1 / 30;
const ROOT_SEED = 12345;

// Camera (UI-SPEC §Camera) — within ±20% tuning budget at executor discretion.
const CAMERA_LERP = 0.08;
const CAMERA_DEADZONE_W = 48;
const CAMERA_DEADZONE_H = 48;
const CAMERA_LOOKAHEAD = 40;
/** Gameplay camera zoom level. 2× makes one tile fill 64 CSS px on a 1× display,
 *  which gives characters (radius 8 px world) a comfortable visible size on phones.
 *  Tunable at runtime via DevOverlay or play-test feedback. */
const CAMERA_ZOOM = 2;

// Depth constants (UI-SPEC §Phaser Canvas Visual Hierarchy; RESEARCH Pitfall 6).
const DEPTH_FLOOR = 0;
// const DEPTH_ROADS = 5;      // reserved for future road layer
/**
 * Phase 08.1: depth for authored roof polygons (terracotta building footprints).
 * Sits above the ground surface (0) and road layer (5), below the invisible collision
 * layer (10) and characters (20) so players appear to walk around/over buildings in the
 * top-down view. Roof polygons are drawn once in create() into a static Graphics object.
 */
const DEPTH_ROOF = 8;
const DEPTH_WALL = 10;
/**
 * Phase 08.1 Item 2: tree trunk ellipses render above the ground + obstacles
 * but BELOW characters so the player appears to walk in front of a trunk.
 * Canopy stays above characters at DEPTH_CANOPY (30).
 */
const DEPTH_TRUNK = 12;
// DEPTH_LIGHT_MASK = 15 imported from '../lighting/mask' — slots between WALL
// and CHARACTER so the mask dims walls but the player silhouette renders above
// the darkness (CONTEXT.md D-09).
const DEPTH_CHARACTER = 20;
/**
 * P3 (Plan 03-07) — depth slot for debug HUD highlights like the foxguard
 * debug ring. Placed above the character so the ring is always visible during
 * playtest; gated at runtime by isDebugEnabled().
 */
const DEPTH_HUD_HIGHLIGHT = 25;
/**
 * Phase 08 Plan 04 (D-11, UI-SPEC §Depth Contract) — canopy + roof-overhang
 * layers render ABOVE characters so players walk visually beneath tree crowns
 * and roof eaves until the group fades on proximity. Exported so tests can
 * assert the value directly (MAP2-03, MAP2-04).
 */
export const DEPTH_CANOPY = 30;

/** P3 Plan 03-07: team-switch camera.pan timings (UI-SPEC §Diegetic Team-Switch Contract). */
const TEAM_SWITCH_PAN_TO_PLINTH_MS = 2000;
const TEAM_SWITCH_HOLD_MS = 3000;
const TEAM_SWITCH_PAN_TO_PLAYER_MS = 2000;
const TEAM_SWITCH_PLAYER_PAN_DELAY_MS =
  TEAM_SWITCH_PAN_TO_PLINTH_MS + TEAM_SWITCH_HOLD_MS;

/**
 * Maximum spiral radius (in tiles) searched by snapToPassableTile when trying
 * to nudge a candidate spawn off a blocked tile. Large enough to escape any
 * single building (the biggest authored building is ~12 tiles across) but
 * bounded so a pathologically-surrounded candidate fails loudly (returns the
 * original position) rather than hanging the create() path.
 */
const SPAWN_SNAP_MAX_RADIUS_TILES = 16;

/**
 * UAT Test 2 fix (hunters-frozen-at-spawn): nudge a world-pixel position to
 * the nearest walkable tile centre. If the candidate tile is already walkable
 * the original position is returned unchanged — callers who supply an already-
 * valid spawn pay nothing. Otherwise a square-spiral scan (radius 1..N tiles)
 * finds the nearest walkable tile and returns its centre in world pixels.
 *
 * Returns the original position if no walkable tile exists inside
 * SPAWN_SNAP_MAX_RADIUS_TILES tiles (map is pathological — fail loud later via
 * the existing pathfinding smoke test).
 *
 * Phase 11 UAT fix (260704-ouy — hunter spawning inside wall polygon): a tile
 * the grid calls walkable can still sit inside a building wall polygon (Phase
 * 08.1 polygon collision). The candidate is now accepted only when it is BOTH
 * tile-grid-walkable AND polygon-clear (`!isPositionBlocked`), using the same
 * predicates the movement resolver enforces in POLYGON MODE so spawn-validity
 * exactly matches movement-validity. The initial in-place position is subject to
 * the same combined check — an already-polygon-blocked spawn is NOT returned
 * as-is even if its tile is marked walkable.
 */
function snapToPassableTile(pos: Vec2, mapData: MapData): Vec2 {
  const tx = Math.floor(pos.x / mapData.tileWidth);
  const ty = Math.floor(pos.y / mapData.tileHeight);
  // Accept in-place only when tile-walkable AND not polygon-blocked.
  if (
    isWalkable(mapData.grid, tx, ty) &&
    !isPositionBlocked(pos.x, pos.y, P1_CHARACTER_RADIUS, mapData)
  ) {
    return pos;
  }
  for (let r = 1; r <= SPAWN_SNAP_MAX_RADIUS_TILES; r++) {
    // Scan the square ring at radius r. Iterate the full bounding box and
    // skip the interior — simpler than four separate edge walks and still
    // O(r) cells examined per ring.
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.abs(dx) !== r && Math.abs(dy) !== r) continue; // interior
        const cx = tx + dx;
        const cy = ty + dy;
        // Evaluate the polygon check at the tile-centre world pixel (the value
        // that would be returned) so the snap result is guaranteed movement-valid.
        const centerX = cx * mapData.tileWidth + mapData.tileWidth / 2;
        const centerY = cy * mapData.tileHeight + mapData.tileHeight / 2;
        if (
          isWalkable(mapData.grid, cx, cy) &&
          !isPositionBlocked(centerX, centerY, P1_CHARACTER_RADIUS, mapData)
        ) {
          return { x: centerX, y: centerY };
        }
      }
    }
  }
  return pos; // no walkable tile in range — return input unchanged.
}

export class GameScene extends Phaser.Scene {
  private accumulator = 0;
  private prevState!: WorldState;
  private currState!: WorldState;
  private readonly controller = localInputController; // Plan 04 joystick pushes intents
  private character!: CharacterSprite;
  private simTimeMs = 0;
  /**
   * Cached MapData from extractMapData() — passed to tick() every frame so sim-side
   * wall clamping (Plan 01-07 Task 5) runs. Null before create() finishes; the
   * update() call cannot fire before Phaser's scene lifecycle has finished create(),
   * so the non-null invariant holds at every update site.
   */
  private cachedMapData: MapData | null = null;
  /** P2 Plan 02-03: light mask compositor. Instantiated in create(); redrawn
   *  each render frame via drawLightMask(); skips stamps on unchanged sim tick. */
  private lightMask: LightMask | null = null;
  /**
   * P3 Plan 03-07: per-entity character sprites keyed by entity id. The player
   * sprite 'e0' is created in create() and stored here for camera follow +
   * interpolation; other entities (e.g. hunter-0) get lazy sprites as they
   * first appear in the world.
   */
  private sprites: Record<string, CharacterSprite> = {};
  /**
   * P3 Plan 03-07: last spotId emitted via 'hide-candidate-changed'. Used to
   * diff between ticks and emit only on change (HUD ergonomics — no per-tick
   * spam).
   */
  private lastHideCandidateSpotId: string | null = null;
  /**
   * P3 Plan 03-07: foxguard debug-ring Graphics. Lazily created on
   * first frame where a hunter has a non-null foxguardConditionsStreakStartTick.
   * Cleared + redrawn each render frame so stale rings from cleared streaks
   * disappear immediately. Only drawn when isDebugEnabled() returns true.
   */
  private foxguardDebugRing: Phaser.GameObjects.Graphics | null = null;

  /**
   * P4 Plan 04-04 (D-11): per-entity awareness telegraph sprites.
   * Keyed by entity id. Spawned/swapped/removed by updateAwarenessSprite()
   * in response to 'awareness-changed' events forwarded from the sim.
   * One Image per entity — T-04-11 DoS mitigation: setTexture reuse, no
   * per-tick allocation. Follows the character sprite position each frame
   * with a -24 px y-offset so the glyph floats above the character head.
   */
  private awarenessSprites: Record<string, Phaser.GameObjects.Image> = {};

  /**
   * P4 Plan 04-11: unsubscribe handle for the 'round-setup-confirmed' renderBus
   * subscription registered in create(). GameScene is a singleton per page load
   * so the handle is not invoked today; reserved for future scene-shutdown paths
   * (Phaser `shutdown`/`destroy`) that would otherwise leak the listener.
   */
  private roundSetupOff?: () => void;

  /**
   * Phase 10 Plan 03: snapped player spawn position captured at create() so the
   * round-setup-confirmed handler can pass it to buildRosterState without
   * re-deriving it from mapData (which gives the pre-snap raw point).
   */
  private _spawnPosition: Vec2 = { x: 0, y: 0 };

  /**
   * P4 Plan 04-12: render-layer sim pause flag.
   *
   * Set to true immediately after `round-setup-propose` is emitted in create()
   * (round 1 only — propose is emitted once per page load). While true, update()
   * short-circuits the accumulator drain so `tick()` is NOT called and
   * `currState.tick` does not advance. The headstart countdown therefore
   * freezes from the user's perspective until they press Confirm.
   *
   * Cleared in the 'round-setup-confirmed' handler along with a
   * `this.accumulator = 0` reset so any delta that built up during the overlay
   * does NOT flood the pump with a burst of catch-up ticks on the first frame
   * after dismissal.
   *
   * Sim-layer determinism invariant: this flag lives exclusively on the render
   * side. `tick()` is unaware of it. The golden-hash guard
   * (src/sim/__tests__/sim.golden-hash.test.ts) drives `tick()` directly and is
   * unaffected by construction. Plan 04-12 touches zero sim code paths.
   */
  private roundSetupPending = false;

  /**
   * Phase 6 D-01 / Pitfall 11 — DOM-follow per-frame world→screen helper for
   * BubbleLayer + camera-bias. Reused scratch Vector2 avoids allocation each
   * rAF frame while three bubbles read sprite positions; a fresh Vector2 per
   * call would generate ~180 short-lived allocations per second under cap-3.
   */
  private spriteScreenPosScratch = new Phaser.Math.Vector2();

  /**
   * Phase 6 D-03 — current camera follow-offset bias applied by
   * camera-bias.ts. Tracked here (not just on the camera) so a future stack
   * of biases / debug readout can introspect; today it just mirrors the live
   * `cameras.main.followOffset` set via setFollowOffset().
   */
  private cameraBiasOffset: { x: number; y: number } = { x: 0, y: 0 };

  /**
   * Phase 6 D-13 (Plan 06-07) — per-frame sprite shiver delta applied to the
   * player's interpolated position before setPosition. Driven by
   * FearFeedbackLayer's per-rAF sin/cos pair off Entity.fear (D-14, D-15).
   * Lives on the render side only — the sim's authoritative position is
   * never perturbed (P6-Pitfall E mitigation hook). Reset to zero when fear
   * drops below the nervous gate or during caught-fadeout.
   */
  private playerShiverDelta: { x: number; y: number } = { x: 0, y: 0 };

  /**
   * Phase 09 Plan 09-04 — per-entity sprint flag mirror, populated from the
   * 'entity-moved' render-bus event (T-09-04-01: single subscription, O(1) per frame).
   * Keyed by entity id (e.g. 'e0', 'e1', …).
   */
  private _sprintState: Record<string, boolean> = {};

  /**
   * Phase 11 Plan 11-04 (D-01): latest awareness level per entity, maintained
   * by subscribing to 'awareness-changed' renderBus events in create().
   * Snapshotted at ai-debug emit time (accumulate-then-snapshot pattern,
   * same as `fears`). Keyed by entity id.
   */
  private latestAwareness = new Map<string, string>();

  /**
   * UAT Test 2 fix: entity ids of AI-controlled entities (hunter/hider
   * simulated slots, populated at create()). Used by update() to iterate the
   * registry once per tick and build IntentBundles from each controller's
   * cached intent. Dogs/adults are driven by controllers registered in
   * createInitialWorldState() so they are included here too.
   */
  private aiEntityIds: string[] = [];

  /**
   * P7 Plan 07-07 (UI-05 / D-08): spectator camera state. When the local player
   * ('e0') is caught, the camera follows a FREE hider instead of the dead player
   * sprite. `watchedHiderId` is the entity currently followed (null = not
   * spectating / following e0 as normal). The set of candidates is the id-SORTED
   * list of free hiders (07-UI-SPEC determinism requirement); advancing cycles
   * round-robin and an auto-fallback re-targets immediately if the watched hider
   * is itself caught (Pitfall C — never freeze the camera on a caught sprite).
   *
   * Pure render: this reads sim state (status/role/position) to choose a follow
   * target and NEVER writes back. The golden hash is untouched by construction.
   */
  private watchedHiderId: string | null = null;
  /**
   * P7 Plan 07-07: set by the spectator gesture handler (tap or ~24px swipe) to
   * request advancing to the next free hider on the next updateSpectatorCamera()
   * pass. Consumed (reset to false) when the advance is applied.
   */
  private spectatorAdvanceRequested = false;
  /**
   * P7 Plan 07-07: pointer-down coords for the spectator tap-vs-swipe gesture.
   * Reuses the P1 8px tap deadzone sensibility (a tap < 8px OR a swipe > 24px
   * advances the watched hider; the dead band in between is ignored as an
   * accidental drag).
   */
  private spectatorPointerDown: { x: number; y: number } | null = null;
  /**
   * P7 Plan 07-07: prefers-reduced-motion snapshot taken once in create(). When
   * true the spectator camera target-swap uses lerp 1,1 (instant cut) instead of
   * the smoothed CAMERA_LERP follow. jsdom has no matchMedia → resolves false.
   */
  private reducedMotion = false;
  /**
   * Phase 08 Plan 04 (MAP2-03/MAP2-04, D-09/D-10): roof-overhang fade groups,
   * built at create() from the roof-overhang tile layer. Each group is one
   * connected component (flood-fill of non-empty tiles); proximity-checked each
   * frame in update() against this.character's interpolated render position.
   * Per-group tween: 0.3 alpha in 150 ms on enter, 1.0 in 400 ms on exit.
   *
   * Phase 08.1 update: the `canopyLayer` (tile-based tree canopy) is now set
   * invisible and excluded from fadeGroups — polygon canopies in
   * `polygonCanopyGroups` replace it with seeded organic shapes.
   */
  private fadeGroups: FadeGroup[] = [];

  /**
   * Phase 08.1 (Item 1): per-tree polygon canopy fade groups. Built at create()
   * from `mapData.trees` using seeded alea RNG (seed = tree pixel centre) so
   * each tree's shape is stable across page reloads and unique between trees.
   * Proximity-checked at render cadence via point-in-ellipse; alpha-tweened
   * using the same nextFadeState machine as `fadeGroups` above.
   * Empty array until create() populates it; gracefully no-op on placeholder maps.
   */
  private polygonCanopyGroups: PolygonCanopyGroup[] = [];

  /**
   * Phase 08.1 Item 1 (fix): per-roof polygon fade groups. Built at create() from
   * `mapData.roofs`. Each roof polygon is drawn at DEPTH_CANOPY (30) — above
   * characters — and fades to ~0.3 alpha when the player walks inside it (point-in-
   * polygon proximity check), restoring on exit. Replaces the blocky roof-overhang
   * tile layer with crisp terracotta polygons that have the same walk-under fade.
   * Empty array until create() populates it.
   */
  private polygonRoofGroups: PolygonRoofGroup[] = [];

  constructor() {
    super('GameScene');
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#0d1f2d');

    // --- Tilemap ---
    const map = this.make.tilemap({ key: 'mouchao' });
    const tileset = map.addTilesetImage('mouchao-tileset');
    if (!tileset) {
      throw new Error('Tileset mouchao-tileset missing from map — check BootScene preload + tileset image name in mouchao.tmj');
    }

    // createLayer() returns TilemapLayer | TilemapGPULayer in Phaser 4; we never
    // pass gpu:true so the runtime value is always TilemapLayer. Cast explicitly.
    const background = map.createLayer('background', tileset, 0, 0) as Phaser.Tilemaps.TilemapLayer | null;
    if (!background) throw new Error('Tile layer "background" missing in mouchao.tmj');
    background.setDepth(DEPTH_FLOOR);
    // Phase 08.1 (polygon-native rendering): the authored surface + roof polygons
    // replace the rasterized background visuals. Hide the tile layer so the crisp
    // polygon fills show beneath characters instead of the blocky rasterized tiles.
    // The background tile layer is still CREATED (kept for map-structure integrity
    // and to avoid breaking the layer-count assertion in GameScene.test.ts) but is
    // invisible at runtime. Collision is unaffected — it runs from the separate
    // collision tile layer (physics-only, also invisible).
    background.setVisible(false);

    const collisionLayer = map.createLayer('collision', tileset, 0, 0) as Phaser.Tilemaps.TilemapLayer | null;
    if (!collisionLayer) throw new Error('Tile layer "collision" missing in mouchao.tmj');
    collisionLayer.setCollisionByProperty({ collides: true });
    collisionLayer.setDepth(DEPTH_WALL);
    // Phase 08 D-04/D-12: collision layer is physics-only; all wall-face (gid 6),
    // trunk (gid 8), and roof-body (gid 2) VISUALS live on the visible background
    // layer painted in Plan 03. Making collision visible would render 11,846 solid
    // gid-6 fills over all building interiors, covering terracotta-roof gid-2
    // visuals — MAP2-01/02 failure. setCollisionByProperty + Arcade collider are
    // unchanged; extractMapData reads this layer for the walkability grid.
    collisionLayer.setVisible(false);

    // Phase 08 Plan 04 (MAP2-03/MAP2-04, D-11, UI-SPEC §Layer & Depth Contract):
    // canopy + roof-overhang layers rendered above characters at DEPTH_CANOPY (30).
    // Keep default TilemapLayer (gpu:false) — TilemapGPULayer breaks per-tile alpha
    // (RESEARCH Pitfall 5). Do NOT pass to extractMapData; fade is render-only.
    const canopyLayer = map.createLayer('canopy', tileset, 0, 0) as Phaser.Tilemaps.TilemapLayer | null;
    if (!canopyLayer) throw new Error('Tile layer "canopy" missing in mouchao.tmj');
    canopyLayer.setDepth(DEPTH_CANOPY);
    // Phase 08.1 Item 1: polygon canopies replace tile-based tree canopy visuals.
    // The canopy tile layer is kept (for map structure / layer count) but made
    // invisible — organic blob polygons from mapData.trees render in its place.
    canopyLayer.setVisible(false);

    const roofOverhangLayer = map.createLayer('roof-overhang', tileset, 0, 0) as Phaser.Tilemaps.TilemapLayer | null;
    if (!roofOverhangLayer) throw new Error('Tile layer "roof-overhang" missing in mouchao.tmj');
    roofOverhangLayer.setDepth(DEPTH_CANOPY);
    // Phase 08.1 Item 1 (fix): hide the blocky terracotta eave tiles — the crisp
    // roof polygons rendered at DEPTH_CANOPY via buildPolygonRoofs() replace them.
    // No blocky roof tiles remain on screen; the tile layer is kept for map-structure
    // integrity (layer count assertions) but never drawn.
    roofOverhangLayer.setVisible(false);

    // Phase 08.1 Item 1 fix: tile-based fade groups are no longer needed — the
    // roof-overhang layer is invisible and polygonRoofGroups handle walk-under fade
    // via Graphics + tweenCounter. Assign empty array so the update() loop is a no-op.
    // (buildFadeGroups is NOT called — it would build groups for now-invisible tiles.)
    this.fadeGroups = [];

    // --- Sim bridge: extract plain MapData, push to sim ---
    const mapData = extractMapData(map, collisionLayer);
    setMapData(mapData);
    // Cache locally so update() can pass it to tick() every frame for sim-side
    // wall clamping (Plan 01-07 Task 5). getMapData() from the sim-side singleton
    // would also work but the direct reference avoids a lookup per tick.
    this.cachedMapData = mapData;

    // --- Phase 08.1: polygon-native rendering ---
    //
    // Depth contract (phase 08.1 Item 1 revised):
    //   surfaces  → DEPTH_FLOOR (0):   ground terrain beneath everything.
    //   obstacles → DEPTH_ROOF (8):    low solid props above ground, below characters.
    //   fences    → DEPTH_ROOF (8):    thin line obstacles, same depth tier.
    //   roofs     → DEPTH_CANOPY (30): building polygons ABOVE characters with
    //                                   walk-under proximity fade (replaces the blocky
    //                                   roof-overhang tile layer hidden above).
    //
    // Performance note: drawn once in create(); Phaser 4 retains Graphics commands in
    // the WebGL buffer so subsequent frames replay without re-issuing draw calls.
    // fillPoints / path API uses Earcut triangulation for concave polygon support.
    //
    // Optional fields: sim tests build minimal MapData without render fields;
    // fall back to empty arrays so create() is a no-op on placeholder maps.
    this.drawSurfaces(mapData.surfaces ?? []);
    this.drawObstaclesAndFences(mapData.obstacles ?? [], mapData.fences ?? []);

    // Phase 08.1 Item 2: draw tree trunks as filled brown ellipses at DEPTH_TRUNK (12).
    // Each trunk is sized to 22% of the canopy semi-axes (min 3 px), stored on the
    // TreeObject as trunkRx/trunkRy so this render and the sim collision share the
    // SAME ellipse. Trunks sit above ground/obstacles and below characters — the
    // player walks visually in front of the trunk; the canopy at DEPTH_CANOPY (30)
    // sits above the player.
    this.buildTreeTrunks(mapData.trees ?? []);

    // Phase 08.1 Item 1: build per-tree polygon canopies from mapData.trees.
    // Each tree gets its own Graphics object drawn once here; alpha is tweened
    // in update() on player proximity. Trees optional (sim tests have no trees).
    this.buildPolygonCanopies(mapData.trees ?? []);

    // Phase 08.1 Item 1 (fix): build per-roof polygon fade groups from mapData.roofs.
    // Each roof polygon is drawn at DEPTH_CANOPY with walk-under proximity fade —
    // a crisp terracotta fill that fades when the player walks inside it. This
    // replaces the now-hidden blocky roof-overhang tile layer (setVisible(false) above).
    this.buildPolygonRoofs(mapData.roofs ?? []);

    // Spawn position: from the Tiled `spawn.player` point or map centre as fallback.
    const spawnRaw: Vec2 = mapData.playerSpawn ?? {
      x: map.widthInPixels / 2,
      y: map.heightInPixels / 2,
    };
    const spawn: Vec2 = snapToPassableTile(spawnRaw, mapData);

    // P3 Plan 03-08 D-27 (UAT Test 8 follow-up): DevOverlay's character-
    // picker persists the selected player character id in sessionStorage
    // under `hunter:dev.character`. Consume it at boot so reloading after
    // picking a new character actually swaps the player's profile. Unknown
    // or missing values fall back to `charlie` (the documented default).
    // Wrapped in try/catch because sessionStorage throws in sandboxed
    // iframes.
    let playerCharacterId = DEFAULT_CHARACTER_ID;
    try {
      const stored = sessionStorage.getItem('hunter:dev.character');
      if (stored && hasProfile(stored)) {
        playerCharacterId = stored;
      }
    } catch {
      // sessionStorage unavailable — stick with the default.
    }

    // --- P4 Plan 04-11: compose the initial roster before sim init ---
    // composeInitialRoster produces the full 9-character roster (4v4 + 1
    // benched in v1 with bennyLocked=true — D-36, OQ-5 interpretation). The
    // authored composition feeds two consumers:
    //   1. The propose event payload so RoundSetup.tsx's mount gate opens
    //      and it can render drag-drop chips for all 9 characters.
    //   2. The 4 simulated entity slots (e0/hider-1/hider-2/hunter-0) below
    //      by matching composition characterIds into the hard-coded entity
    //      ids. The remaining ~5 composed characters are ROSTER-ONLY in v1 —
    //      they appear as chips in the overlay but have no sim entity yet.
    //      This keeps the entity-spawn set at 4 (scope boundary — expanding
    //      to 9 simulated entities is a separate plan when multi-entity AI
    //      is stress-tested).
    const allPlayableIds = listProfileIds();
    const rng = createRng(ROOT_SEED);
    const composition = composeInitialRoster(
      playerCharacterId,
      'hider', // Player starts on hider team at boot (D-02 / 'e0' is always hider).
      allPlayableIds,
      true, // bennyLocked = true in v1 (D-36). P5 admin flow swaps this dynamic.
      null, // lastBenchedNpcId: first round, no prior bench.
      rng.round,
    );

    // --- Phase 10 Plan 03 (ROSTER-01..04): 8-entity 4v4 spawn via buildRosterState ---
    // buildRosterState maps the composed 4v4 roster to entity ids, calls
    // computeSpawnLayout for zone-spread hunters + plinth-cluster hiders,
    // snaps each position to a passable tile, creates the world state, and
    // registers 7 NPC AI controllers (clearAiControllers first to avoid
    // dangling entries — T-10-03-CTRL / D-09). This is the boot invocation
    // (round 1); the round-setup-confirmed handler (Seam E) calls it again
    // on user confirm with the finalised roster.
    this._spawnPosition = spawn;
    const worldState = this.buildRosterState(
      { hunters: composition.hunters, hiders: composition.hiders },
      mapData,
      playerCharacterId,
      spawn,
      1,
    );
    this.prevState = worldState;
    this.currState = worldState;

    // --- P4 Plan 04-11: open RoundSetup.tsx's mount gate ---
    // Emit the composed roster onto the render bus so RoundSetup's
    // 'round-setup-propose' subscriber flips its internal `state` from null to
    // the proposed assignments. The UI renders 3 columns + 9 chips; the user
    // can drag chips between columns before pressing Confirm.
    // Plan 10-05 (WR-04): include playerCharacterId so RoundSetup.tsx can
    // lock the player's own chip to the hider column, preventing the CR-01
    // corrupted-roster path from being reachable via normal drag gestures.
    renderBus.emit('round-setup-propose', {
      hunters: composition.hunters,
      hiders: composition.hiders,
      benched: composition.benched,
      playerCharacterId,
    });

    // P4 Plan 04-12: pause the render-layer sim pump while the overlay is
    // mounted. Cleared in the 'round-setup-confirmed' handler below. Round 1
    // only — propose is emitted once per page load, so round 2+ never re-arm
    // the pause (D-37 mount gate enforces this in RoundSetup.tsx).
    this.roundSetupPending = true;

    // P4 Plan 04-12 (fix): emit one synthetic sim-tick so RoundSetup.tsx's
    // mount gate sees roundNumber=1 / roundPhase='headstart'. Without this,
    // the pause above prevents update()'s sim-tick emit from ever running,
    // RoundSetup's sim-tick subscriber never fires, roundNumber stays null,
    // and the overlay never mounts (chicken-and-egg with the pause).
    {
      const p0 = this.currState.entities['e0'];
      const cid0 = p0?.characterId ?? DEFAULT_CHARACTER_ID;
      const prof0 = hasProfile(cid0) ? getProfile(cid0) : getProfile(DEFAULT_CHARACTER_ID);
      renderBus.emit('sim-tick', {
        tick: this.currState.tick,
        simTimeMs: this.simTimeMs,
        seed: ROOT_SEED,
        round: this.currState.round,
        playerStamina: p0?.stamina ?? 1,
        playerRole: p0?.role ?? 'hider',
        playerStatus: p0?.status ?? 'free',
        characterId: cid0,
        playerAbilities: prof0.abilities,
        characterDisplayName: prof0.id.charAt(0).toUpperCase() + prof0.id.slice(1),
        // P6 (Plan 06-05): footstep distance-cull inputs. Player position +
        // tile width are emitted from the live sim state so the audio
        // footsteps module can cache them per sim-tick.
        playerPosition: p0?.position ?? null,
        mapTileWidth: this.cachedMapData?.tileWidth ?? 32,
        // P6 (Plan 06-12, UAT Gap 4): per-entity status snapshot for the
        // BubbleLayer hidden-hider speech-silence guard. Pure render-side
        // derivation — sim purity preserved. Hiders only — see sim-tick emit.
        entityStatuses: Object.fromEntries(
          Object.entries(this.currState.entities)
            .filter(([, e]) => e.role === 'hider')
            .map(([id, e]) => [id, e.status]),
        ),
        // P7 Plan 07-07: never spectating at boot (player just spawned free).
        spectatorWatching: null,
      });
    }

    // Subscribe to 'round-setup-confirmed': when the user presses Confirm in
    // RoundSetup.tsx, the UI emits the user-chosen roster. We apply the role
    // override to the four spawned entities by matching on characterId.
    // Dogs/adults are unaffected (they are NPCs with fixed roles).
    // Benched characterIds: leave the entity's role as-is (the entity stays
    // on its current team — there is no separate 'benched' role).
    // This mutation happens during headstart (before the first active tick),
    // which is well before catch detection kicks in (tick.ts gates catch on
    // phase === 'active'). If the user never confirms, headstart expires with
    // the composition defaults already applied above.
    this.roundSetupOff = renderBus.on('round-setup-confirmed', (payload) => {
      // P4 Plan 04-12: clear the sim pause FIRST so if anything below
      // throws, the pump still resumes. Reset accumulator to 0 — any wall-
      // clock delta that built up during the overlay must NOT flood the
      // pump with a burst of catch-up ticks on the first post-dismiss frame
      // (headstart countdown would then jump forward visibly).
      this.roundSetupPending = false;
      this.accumulator = 0;

      // Phase 10 Plan 03 (ROSTER-03 / Seam E): spawn-driven confirm.
      // Rebuild the entire world from the confirmed 4v4 roster instead of
      // mutating pre-spawned entity roles (the "spawn-then-flip" anti-pattern
      // forbidden by D-06/D-07). buildRosterState handles clearAiControllers +
      // 7× initAiController (T-10-03-CTRL / D-09) so no dangling controller
      // remains after the rebuild.
      const confirmedState = this.buildRosterState(
        payload,
        this.cachedMapData!,
        this.currState.entities['e0']?.characterId ?? DEFAULT_CHARACTER_ID,
        this._spawnPosition,
        this.currState.round.roundNumber,
      );
      this.prevState = confirmedState;
      this.currState = confirmedState;
    });
    // Suppress "declared but never read" — roundSetupOff is reserved for
    // future scene-shutdown paths (Phaser `shutdown`/`destroy` hooks). Today
    // GameScene is a singleton per page load so the handle is never invoked.
    void this.roundSetupOff;

    // --- P7 Plan 07-07: reduced-motion snapshot (D-08 instant-cut path) ---
    // Read once; the spectator target-swap uses lerp 1,1 when reduce is set so
    // the camera cuts instead of glides. Guarded for jsdom (no matchMedia).
    this.reducedMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // --- P7 Plan 07-07 (D-02, Pitfall D): in-game pause seam ---
    // Reuse the EXISTING ticker-pause mechanism (MOVE-08 visibilitychange uses
    // game.loop.sleep/wake). The PauseButton (Plan 09) + App (Plan 10) emit
    // pause-requested / resume-requested; we drive the same loop here. We do NOT
    // invent a second pause path — there is one ticker, one pause.
    // Phase 09 Plan 09-04: mirror the per-entity sprint flag from entity-moved
    // events into _sprintState so the per-frame animation driver can pick it up without reading
    // sim state directly (T-09-04-01: single subscription, O(1) lookup per frame).
    renderBus.on('entity-moved', (ev) => {
      this._sprintState[ev.entityId] = ev.sprint;
    });

    // Phase 11 Plan 11-04 (D-01): accumulate latest awareness level per entity
    // for the ai-debug npcBranches snapshot. Same pattern as fears (snapshot at
    // emit time). Subscribes here so the map is always populated before the
    // first ai-debug emit in update().
    renderBus.on('awareness-changed', (ev) => {
      this.latestAwareness.set(ev.entityId, ev.level);
    });

    renderBus.on('pause-requested', () => {
      const loop = this.game?.loop as unknown as {
        sleep?: () => void;
        pause?: () => void;
      } | undefined;
      if (typeof loop?.sleep === 'function') loop.sleep();
      else if (typeof loop?.pause === 'function') loop.pause();
    });
    renderBus.on('resume-requested', () => {
      const loop = this.game?.loop as unknown as {
        wake?: () => void;
        resume?: () => void;
      } | undefined;
      if (typeof loop?.wake === 'function') loop.wake();
      else if (typeof loop?.resume === 'function') loop.resume();
    });

    // --- P7 Plan 07-07 (UI-05): spectator advance gesture ---
    // While spectating (watchedHiderId !== null), a tap (<8px) or a swipe
    // (>24px) cycles to the next free hider. The 8..24px dead band is ignored
    // as an accidental drag (reuses the P1 tap-vs-drag deadzone sensibility).
    // input is undefined under the unit-test Phaser mock — optional-chain so
    // create() stays test-safe.
    this.input?.on?.('pointerdown', (p: { x: number; y: number }) => {
      this.spectatorPointerDown = { x: p.x, y: p.y };
    });
    this.input?.on?.('pointerup', (p: { x: number; y: number }) => {
      const down = this.spectatorPointerDown;
      this.spectatorPointerDown = null;
      if (this.watchedHiderId === null || !down) return; // only while spectating
      const dist = Math.hypot(p.x - down.x, p.y - down.y);
      if (dist < 8 || dist > 24) this.spectatorAdvanceRequested = true;
    });

    // --- Character sprite + physics ---
    // Player 'e0' is always hider at round 1 boot (D-02). Tinted via role param
    // so hunters/hiders are visually distinguishable during UAT (Test 2 follow-up).
    // WR-02: seed the sprite from the sim-resolved e0 entity position (plinth
    // cluster after buildRosterState / computeSpawnLayout hider branch, D-04)
    // rather than the raw Tiled playerSpawn point. When both agree the effect is
    // zero; when they differ (plinth centre ≠ Tiled spawn) the sprite and entity
    // start at the same world coordinate, eliminating the round-start camera slide.
    const e0Pos = this.currState.entities['e0']?.position ?? spawn;
    this.character = new CharacterSprite(this, e0Pos.x, e0Pos.y, 'hider', 'e0');
    this.character.setDepth(DEPTH_CHARACTER);
    // P3 Plan 03-07: register the player sprite in the per-entity map so the
    // camera-follow + interpolation paths and the render-tick sprite-sync loop
    // share a single source of truth.
    this.sprites['e0'] = this.character;
    this.physics.add.existing(this.character);
    // Size the Arcade body to match the visual 8-px circle. Phaser 4's
    // default body for a Container is 1×1 at origin, which produces a no-op
    // collider against 32×32 wall tiles (Plan 01-07, UAT Test 1 Gap B).
    // Must run BEFORE physics.add.collider so the first solver tick sees the
    // correctly-sized body. Regression-locked by:
    //   - src/render/sprites/__tests__/character-sprite.test.ts (method behaviour)
    //   - src/render/scenes/__tests__/GameScene.test.ts      (call-site invocation)
    this.character.configurePhysicsBody();
    this.physics.add.collider(this.character, collisionLayer);

    // --- P2 Plan 02-03: light mask at depth 15 ---
    // Sized to the full map; follows the camera via setScrollFactor(1). Placed
    // AFTER the collision layer + character so BootScene preload keys for the
    // three glow PNGs (light.permanent-glow / motion-glow / torch-cone) are
    // already resolved by the time LightMask.redraw() stamps them.
    this.lightMask = new LightMask(this, map.widthInPixels, map.heightInPixels);

    // --- Camera (RESEARCH Pattern 6) ---
    this.cameras.main.setZoom(CAMERA_ZOOM);
    this.cameras.main.startFollow(this.character, true, CAMERA_LERP, CAMERA_LERP);
    this.cameras.main.setDeadzone(CAMERA_DEADZONE_W, CAMERA_DEADZONE_H);
    this.cameras.main.setBounds(0, 0, map.widthInPixels, map.heightInPixels);
    // Initial follow-offset uses facing=0.
    //
    // Camera-lookahead sign convention — camera LEADS player (MOVE-03).
    //
    // Phaser typedef for setFollowOffset: "The values are SUBTRACTED from the
    // target's position during the Cameras update step." — so the camera
    // centre = target.position - followOffset. For the camera to LEAD the
    // player (character at trailing edge of viewport, reveal road ahead in
    // the facing direction), we need (target - offset) to be offset in the
    // +facing direction, which means offset itself must be in the -facing
    // direction: offset = (-cos(facing), -sin(facing)) * CAMERA_LOOKAHEAD.
    //
    // At facing=0 (screen-RIGHT): offset = (-LOOKAHEAD, 0). Camera centre =
    // player.x - (-LOOKAHEAD) = player.x + LOOKAHEAD — camera sits LOOKAHEAD
    // pixels to the RIGHT of the player, so the player appears on the LEFT of
    // the viewport and the road ahead is visible. CAMERA LEADS.
    this.cameras.main.setFollowOffset(-Math.cos(0) * CAMERA_LOOKAHEAD, -Math.sin(0) * CAMERA_LOOKAHEAD);

    // --- Smoke test (MAP-13) ---
    this.runSmokeTest(mapData);

    // --- P3 Plan 03-07 (UAT Test 6 ergonomics): authored hiding-spot
    // markers. One-shot draw: small amber circle + size glyph at each spot so
    // the player can SEE where the Hide button will arm. Ships in production
    // but gated at runtime by isDebugEnabled() (?debug=1 or localStorage).
    // Depth above the light mask so the dots stay visible in dark areas.
    if (isDebugEnabled()) {
      const markers = this.add.graphics();
      markers.setDepth(DEPTH_HUD_HIGHLIGHT);
      for (const spot of mapData.hidingSpots) {
        // Large spots get a 10 px amber disc; small spots get a 6 px teal disc
        // so the size gate (HIDE-02) is legible at a glance without opening
        // the DevOverlay.
        const isLarge = spot.size === 'large';
        const radius = isLarge ? 10 : 6;
        const color = isLarge ? 0xe8a838 : 0x5cb8b4;
        markers.fillStyle(color, 0.85);
        markers.fillCircle(spot.position.x, spot.position.y, radius);
        markers.lineStyle(1.5, 0x0d1f2d, 1);
        markers.strokeCircle(spot.position.x, spot.position.y, radius);
      }
    }

    // --- Plinth ring (debug session cascade-prevention 2026-04-28) ---
    // Visible solid circular obstacle at the plinth zone centroid. Liberators
    // must touch this ring to trigger free-all; captives are parked at its
    // outer boundary. Sim-side collision in src/sim/movement.ts treats the
    // ring as un-enterable. The 48-px radius matches P3_PLINTH_RING_RADIUS_PX.
    {
      const plinth = mapData.zones.get('plinth');
      if (plinth) {
        const center = zoneCenter(plinth);
        const ring = this.add.graphics();
        ring.setDepth(DEPTH_HUD_HIGHLIGHT - 1);
        // Solid amber fill so the ring reads as a fixture, not a marker. The
        // outer outline is darker so it stays legible on the warm palette.
        ring.fillStyle(0xc78a2a, 0.55);
        ring.fillCircle(center.x, center.y, 48);
        ring.lineStyle(2, 0x4a2f17, 1);
        ring.strokeCircle(center.x, center.y, 48);
      }
    }

    // --- Emit zone-label payload for DevOverlay (dev-only consumer) ---
    const zoneLabels: Array<{ id: string; center: { x: number; y: number } }> = [];
    for (const [id, zone] of mapData.zones) {
      zoneLabels.push({ id, center: zoneCenter(zone) });
    }
    renderBus.emit('zone-labels', { zones: zoneLabels });

    // Phase 6 Plan 04 (WARNING-4) — announce scene readiness to React layers.
    // PhaserGame.tsx subscribes once at module-load to cache `this` for
    // BubbleLayer.getSpriteScreenPos and camera-bias.ts.setCameraBias. Emit
    // LAST in create() so `this.sprites['e0']` and the camera follow chain
    // are fully wired before any subscriber dereferences the scene.
    EventBus.emit('current-scene-ready', this);
  }

  /**
   * Phase 10 Plan 03 (ROSTER-01..04): build a complete 8-entity 4v4 world
   * state from a confirmed (or proposed-default) roster payload. Used from
   * both create() (boot) and the round-setup-confirmed handler so the spawn
   * logic exists exactly once (DRY).
   *
   * Steps:
   *  1. Validate 4v4 (T-10-03-ROSTER); fall back to composition defaults if
   *     hunters.length !== 4 or hiders.length !== 4.
   *  2. Map characterIds → stable entity ids:
   *       e0       → playerCharacterId (always hider, D-04)
   *       hider-1..3 → the 3 remaining NPC hider characterIds
   *       hunter-0..3 → all 4 NPC hunter characterIds
   *  3. computeSpawnLayout → zone-spread hunters + plinth-cluster hiders (raw Vec2s).
   *  4. snapToPassableTile on every raw position (D-01/D-04).
   *  5. createInitialWorldState with 8 RoleSpec entries.
   *  6. clearAiControllers (T-10-03-CTRL/D-09) + 7× initAiController.
   *  7. Rebuild this.aiEntityIds (all non-e0 entities; dogs/adults included).
   *
   * @returns A fresh WorldState — assign to both this.prevState and this.currState.
   */
  private buildRosterState(
    payload: { hunters: string[]; hiders: string[] },
    mapData: MapData,
    playerCharacterId: string,
    spawn: Vec2,
    roundNumber: number,
  ): WorldState {
    // ── 1. Validate 4v4 ────────────────────────────────────────────────────
    let hunterCharIds = payload.hunters;
    let hiderCharIds = payload.hiders;
    if (hunterCharIds.length !== 4 || hiderCharIds.length !== 4) {
      // T-10-03-ROSTER: malformed payload — fall back to composed defaults so
      // the scene never spawns a broken world.
      const fallbackRng = createRng(ROOT_SEED);
      const fallback = composeInitialRoster(
        playerCharacterId,
        'hider',
        listProfileIds(),
        true,
        null,
        fallbackRng.round,
      );
      hunterCharIds = fallback.hunters;
      hiderCharIds = fallback.hiders;
    }

    // ── 2. Map characterIds → entity ids ───────────────────────────────────
    const NPC_HIDER_IDS = ['hider-1', 'hider-2', 'hider-3'] as const;
    const NPC_HUNTER_IDS = ['hunter-0', 'hunter-1', 'hunter-2', 'hunter-3'] as const;
    // CR-01 guard: e0 is always pinned to playerCharacterId (D-04). If the
    // player's chip was dragged to the hunter column in RoundSetup,
    // playerCharacterId appears in payload.hunters → hunter-0 would spawn as
    // playerCharacterId (duplicate of e0) AND npcHiderCharIds[3] would be
    // silently dropped. Filter playerCharacterId from BOTH arrays first, then
    // validate the post-filter counts. On mismatch, fall back to composed
    // defaults (same branch as step 1) rather than spawning a broken world.
    hunterCharIds = hunterCharIds.filter((c) => c !== playerCharacterId);
    let npcHiderCharIds = hiderCharIds.filter((c) => c !== playerCharacterId);
    // Valid 4v4 with player in hiders → hunterCharIds.length === 4,
    // npcHiderCharIds.length === 3. Player in hunters → hunterCharIds.length === 3,
    // npcHiderCharIds.length === 4 — both fail the guard below.
    if (hunterCharIds.length !== 4 || npcHiderCharIds.length !== 3) {
      // T-10-05-CR01: corrupted shape (player's characterId was in hunters,
      // or some other mismatch). Fall back to composed defaults; player is
      // guaranteed in hiders (D-04 from composeInitialRoster).
      const cr01Rng = createRng(ROOT_SEED);
      const cr01Fallback = composeInitialRoster(
        playerCharacterId,
        'hider',
        listProfileIds(),
        true,
        null,
        cr01Rng.round,
      );
      hunterCharIds = cr01Fallback.hunters;
      npcHiderCharIds = cr01Fallback.hiders.filter((c) => c !== playerCharacterId);
    }

    // ── 3. Compute zone-spread hunters + plinth-cluster hiders ─────────────
    const spawnRng = makeSpawnRng(ROOT_SEED, roundNumber);
    const rawPositions = computeSpawnLayout({
      hunterIds: [...NPC_HUNTER_IDS],
      hiderIds: ['e0', ...NPC_HIDER_IDS],
      zones: mapData.zones,
      playerSpawn: spawn,
      rng: spawnRng,
    });

    // ── 4. Snap every raw position to a passable tile ──────────────────────
    const snappedPos = (id: string): Vec2 =>
      snapToPassableTile(rawPositions[id] ?? spawn, mapData);

    // ── 5. Build 8-entry RoleSpec array and create world state ─────────────
    const roles = [
      { id: 'e0',       role: 'hider'  as const, characterId: playerCharacterId,                      spawn: snappedPos('e0') },
      { id: 'hider-1',  role: 'hider'  as const, characterId: npcHiderCharIds[0] ?? DEFAULT_CHARACTER_ID, spawn: snappedPos('hider-1') },
      { id: 'hider-2',  role: 'hider'  as const, characterId: npcHiderCharIds[1] ?? DEFAULT_CHARACTER_ID, spawn: snappedPos('hider-2') },
      { id: 'hider-3',  role: 'hider'  as const, characterId: npcHiderCharIds[2] ?? DEFAULT_CHARACTER_ID, spawn: snappedPos('hider-3') },
      { id: 'hunter-0', role: 'hunter' as const, characterId: hunterCharIds[0]   ?? DEFAULT_CHARACTER_ID, spawn: snappedPos('hunter-0') },
      { id: 'hunter-1', role: 'hunter' as const, characterId: hunterCharIds[1]   ?? DEFAULT_CHARACTER_ID, spawn: snappedPos('hunter-1') },
      { id: 'hunter-2', role: 'hunter' as const, characterId: hunterCharIds[2]   ?? DEFAULT_CHARACTER_ID, spawn: snappedPos('hunter-2') },
      { id: 'hunter-3', role: 'hunter' as const, characterId: hunterCharIds[3]   ?? DEFAULT_CHARACTER_ID, spawn: snappedPos('hunter-3') },
    ];

    const worldState = createInitialWorldState({ rootSeed: ROOT_SEED, roles, mapData, playerSpawn: spawn });

    // ── 6. Register AI controllers for all 7 NPC entities ─────────────────
    // clearAiControllers first (T-10-03-CTRL / D-09): guarantees no dangling
    // controller for a non-existent entity after a full world rebuild.
    clearAiControllers();
    for (const id of NPC_HIDER_IDS) {
      initAiController({ entityId: id, rngSeed: ROOT_SEED, initialRole: 'hider' });
    }
    for (const id of NPC_HUNTER_IDS) {
      initAiController({ entityId: id, rngSeed: ROOT_SEED, initialRole: 'hunter' });
    }

    // ── 7. Rebuild aiEntityIds ─────────────────────────────────────────────
    // Filter to all non-e0 hunter/hider entities, then separately push any
    // dogs/adults (Seam D pattern — preserves the dog/adult push-loop idiom
    // established in the original code so future dog/adult entities still flow
    // through the AI bundle in update()).
    this.aiEntityIds = Object.keys(worldState.entities).filter(
      (id) =>
        id !== 'e0' &&
        worldState.entities[id]!.role !== 'dog' &&
        worldState.entities[id]!.role !== 'adult',
    );
    for (const id of Object.keys(worldState.entities)) {
      const entity = worldState.entities[id]!;
      if (entity.role === 'dog' || entity.role === 'adult') {
        this.aiEntityIds.push(id);
      }
    }

    return worldState;
  }

  /**
   * Phase 6 D-01 — world→screen helper for the React DOM bubble layer.
   * Reuses ONE scratch Vector2 (Pitfall 11) so the rAF loop in BubbleLayer
   * does not allocate per frame. Returns null when the entity has no sprite
   * yet (boot window before lazy NPC sprite spawn) — caller skips the bubble
   * write that frame.
   *
   * Formula is zoom-aware: reads `cam.zoom` (currently CAMERA_ZOOM = 2) so
   * the `(1 - zoom)` term is non-zero and the DOM bubble lands on the correct
   * screen pixel. Works at any zoom level set by CAMERA_ZOOM.
   */
  public getSpriteScreenPos(entityId: string): Phaser.Math.Vector2 | null {
    const sprite = this.sprites[entityId];
    if (!sprite) return null;
    const cam = this.cameras.main;
    this.spriteScreenPosScratch.x =
      (sprite.x - cam.scrollX) * cam.zoom + cam.width * 0.5 * (1 - cam.zoom);
    this.spriteScreenPosScratch.y =
      (sprite.y - cam.scrollY) * cam.zoom + cam.height * 0.5 * (1 - cam.zoom);
    return this.spriteScreenPosScratch;
  }

  /**
   * Phase 6 D-03 — apply a camera follow-offset bias for the player-bubble
   * window. Phaser owns the lerp via setFollowOffset (the existing
   * startFollow lerp factor smooths the transition over the next ~133ms
   * @ 60fps, matching P6_CAMERA_BIAS_LERP_FRAMES = 8). Called by
   * camera-bias.ts on player bubble mount + release.
   */
  public setCameraBias(dx: number, dy: number): void {
    this.cameraBiasOffset.x = dx;
    this.cameraBiasOffset.y = dy;
    this.cameras.main.setFollowOffset(dx, dy);
  }

  /**
   * Phase 6 D-13 (Plan 06-07) — write the per-frame shiver delta applied to
   * the local player sprite next update(). FearFeedbackLayer's per-rAF loop
   * computes (dx, dy) from the local player's fear value (sin/cos at
   * P6_SHIVER_HZ × π) and calls this synchronously. The delta is added to
   * the interpolated sim position; the sim's authoritative entity.position
   * is never touched (P6-Pitfall E render-only mitigation hook).
   */
  public setPlayerShiver(dx: number, dy: number): void {
    this.playerShiverDelta.x = dx;
    this.playerShiverDelta.y = dy;
  }

  update(_time: number, delta: number): void {
    // P4 Plan 04-12: short-circuit while the RoundSetup overlay is mounted.
    // Reset accumulator each frame so delta accrued during the pause does
    // NOT carry over once the flag clears — the confirmed handler also
    // resets it once, but doing it here keeps the invariant local to the
    // gate (any new entry to update() while paused re-asserts the zero).
    // No sim work happens — no events forwarded, no tick() invoked,
    // currState.tick holds steady, headstart countdown freezes.
    if (this.roundSetupPending) {
      this.accumulator = 0;
      return;
    }

    // GAFFER-ON-GAMES ACCUMULATOR — FOUND-02. `delta` stays in the accumulator;
    // `tick()` always receives TICK_SECONDS. DO NOT change this.
    this.accumulator += delta;
    while (this.accumulator >= TICK_MS) {
      const selfEntity = this.currState.entities['e0'];
      if (!selfEntity) break;
      // P4 TODO(Plan 05): build full Perception via src/sim/ai/perception.ts.
      // For now, build a minimal valid P4 Perception so LocalInputController
      // (which ignores all fields) compiles cleanly.
      const mapData = this.cachedMapData;
      const buildMinimalPerception = (entity: typeof selfEntity): Perception => ({
        self: {
          id: entity.id,
          characterId: entity.characterId,
          position: entity.position,
          facing: entity.facing,
          role: entity.role,
          status: entity.status,
          torchOn: entity.torchOn,
          stamina: entity.stamina,
          fear: entity.fear,
          awareness: new Map(),
        },
        visible: [],
        heard: [],
        memory: [],
        map: mapData ?? {
          grid: [], zones: new Map(), tileWidth: 32, tileHeight: 32,
          widthInTiles: 1, heightInTiles: 1, collidableTileCount: 0,
          playerSpawn: null, lights: [], hidingSpots: [],
          fearTriggers: [], dogs: [], adults: [],
        },
        round: {
          phase: this.currState.round.phase,
          roundNumber: this.currState.round.roundNumber,
          tickInRound: this.currState.tick - this.currState.round.startTick,
        },
        rng: () => Math.random(),
        tick: this.currState.tick,
      });
      const perception = buildMinimalPerception(selfEntity);
      const intent = this.controller.decide(perception, TICK_SECONDS);

      // UAT Test 2 fix: build an IntentBundle for every controller-driven
      // entity. Player 'e0' uses localInputController (joystick). AI entities
      // read the previous tick's cached intent from their AIController —
      // tick.ts Stage 16 re-calls decide() with the fresh perception (cadence-
      // gated internally) and updates cachedIntent for the *next* tick. The
      // one-tick lag (~33 ms) is imperceptible and cheaper than duplicating
      // buildPerception on the render side.
      //
      // The first tick after spawn has cachedIntent === ZERO_INTENT (default
      // constructor value), which is exactly the correct behaviour during the
      // headstart movement gate (hunters cannot move during headstart anyway —
      // tick.ts L486-490).
      const intents: IntentBundle[] = [{ entityId: 'e0', intent }];
      for (const entityId of this.aiEntityIds) {
        const entity = this.currState.entities[entityId];
        if (!entity) continue;
        const ctrl = getAiController(entityId);
        if (!ctrl) continue;
        intents.push({ entityId, intent: ctrl.getCachedIntent() });
      }

      // Phase 11 Plan 11-04: the UAT-diag hunter-0-only console.log was removed.
      // Per-NPC state is now surfaced via the ai-debug npcBranches event (D-01).

      const result = tick(
        this.currState,
        intents,
        TICK_SECONDS,
        this.cachedMapData,
      );
      this.prevState = this.currState;
      this.currState = result.state;
      this.simTimeMs += TICK_MS;
      this.accumulator -= TICK_MS;

      // P2 Plan 02-03 / P3 Plan 03-07: forward sim events onto the render bus.
      // P3 extends the switch to cover all 12 new sim event variants.
      // P6 Plan 06-05: pass post-tick state so 'entity-moved' enrichment can
      // read the moved entity's status / characterId / sprint context.
      // P6 sprint detection: compare actual movement distance against
      // baseSpeedMultiplier * tileWidth to flag sprint runs (~1.4x). The
      // detection is heuristic and used purely for footstep gain — golden
      // hash unaffected because this happens on the render side post-tick.
      forwardSimEvents(
        result.events,
        this.currState,
        this.prevState,
        this.cachedMapData?.tileWidth ?? 32,
      );

      // P3 Plan 03-07 D-32 / P4 Plan 04-04 D-11: per-event render-side handling.
      // team-switched → diegetic camera pan (UI-SPEC §Diegetic Team-Switch Contract).
      // awareness-changed → spawn/swap/remove telegraph sprite via updateAwarenessSprite.
      for (const ev of result.events) {
        if (ev.type === 'team-switched' && this.cachedMapData) {
          const plinth = this.cachedMapData.zones.get('plinth');
          const playerNewSpawn = this.currState.entities['e0']?.teamSwitchTarget
            ?? this.cachedMapData.playerSpawn
            ?? { x: 0, y: 0 };
          if (plinth) {
            this.startTeamSwitchAnimation(zoneCenter(plinth), playerNewSpawn);
          }
        }
        if (ev.type === 'awareness-changed') {
          this.updateAwarenessSprite(ev.entityId, ev.level);
        }
      }

      // P3 Plan 03-07 D-37: emit `hide-candidate-changed` when the derived
      // field differs from the last emitted value. HUD (Plan 08) subscribes.
      const cand = this.currState.derived.hideCandidate;
      const newSpotId = cand?.spotId ?? null;
      if (newSpotId !== this.lastHideCandidateSpotId) {
        renderBus.emit('hide-candidate-changed', {
          entityId: cand?.entityId ?? 'e0',
          spotId: newSpotId,
          tick: this.currState.tick,
        });
        this.lastHideCandidateSpotId = newSpotId;
      }

      // P3 Plan 03-08 (WARNING-15 tier-isolation): resolve character-profile
      // fields at emit time so UI components can subscribe to the sim-tick
      // payload without importing PROFILES directly. `playerAbilities` +
      // `characterDisplayName` are the only extras; the rest flow straight
      // from the player entity. Guard against the `e0` entity being
      // undefined (early-boot transient).
      const player = this.currState.entities['e0'];
      const characterId = player?.characterId ?? DEFAULT_CHARACTER_ID;
      const profile = hasProfile(characterId)
        ? getProfile(characterId)
        : getProfile(DEFAULT_CHARACTER_ID);
      const displayName =
        profile.id.charAt(0).toUpperCase() + profile.id.slice(1);

      // P7 Plan 07-07 (UI-05 / D-08): pick/advance the spectator camera target
      // from the post-tick state, then resolve the watched hider's display name
      // render-side (WARNING-15) so the SpectatorBanner reads it off the payload.
      this.updateSpectatorCamera();
      const watched = this.watchedHiderId
        ? this.currState.entities[this.watchedHiderId]
        : null;
      const spectatorWatching = watched
        ? { id: watched.id, name: this.resolveDisplayName(watched.characterId) }
        : null;

      renderBus.emit('sim-tick', {
        tick: this.currState.tick,
        simTimeMs: this.simTimeMs,
        seed: ROOT_SEED,
        round: this.currState.round,
        playerStamina: player?.stamina ?? 1,
        playerRole: player?.role ?? 'hider',
        playerStatus: player?.status ?? 'free',
        characterId,
        playerAbilities: profile.abilities, // WARNING-15: resolved here, not in UI.
        characterDisplayName: displayName,  // WARNING-15: resolved here, not in UI.
        // P6 (Plan 06-05): footstep distance-cull inputs cached by audio module.
        playerPosition: player?.position ?? null,
        mapTileWidth: this.cachedMapData?.tileWidth ?? 32,
        // P6 (Plan 06-12, UAT Gap 4): per-entity status snapshot for the
        // BubbleLayer hidden-hider speech-silence guard. Pure render-side
        // derivation — sim purity preserved.
        // Filter to hiders only so SpectatorBanner.classifyCounts cannot count
        // hunters (status='free') as free hiders, which would inflate the tally
        // above 4 in a 4v4 round. BubbleLayer is unaffected: it checks
        // entityStatuses[hunterId] which is now undefined for hunters, and
        // undefined !== 'hiding' so hunter speech bubbles still show correctly.
        // Debug session hunter-no-chase-facing REOPENED (2026-07-04).
        entityStatuses: Object.fromEntries(
          Object.entries(this.currState.entities)
            .filter(([, e]) => e.role === 'hider')
            .map(([id, e]) => [id, e.status]),
        ),
        // P7 Plan 07-07 (UI-05 / D-08): the free hider the spectator camera is
        // following (null when not spectating). Drives the SpectatorBanner.
        spectatorWatching,
      });

      // P2 Plan 02-03: synthetic per-sim-tick debug snapshot. Fires ONCE per
      // sim tick (30 Hz), not per render frame — DevOverlay renders at sim
      // cadence and the FOV popcount is O(words) cheap. UI-SPEC §DevOverlay
      // P2 extension consumes the exact shape below.
      this.emitLightingDebug();

      // P4 Plan 04-10 (Task 3, D-11): synthetic AI debug snapshot for DevOverlay.
      // Carries blackboard entries + per-entity fear levels. Ships in production
      // but gated at runtime by isDebugEnabled() — only emitted when the debug
      // flag is active (?debug=1 or localStorage['hunter:debug']='1').
      // Phase 11 Plan 11-04 (D-01): extended with npcBranches — per-NPC AI state
      // (branch / torch / awareness / checkedSpots). Built via getAiController +
      // the sanctioned agent cast; awareness snapshot from this.latestAwareness
      // (maintained by the awareness-changed subscriber in create()).
      if (isDebugEnabled()) {
        const fears: Record<string, number> = {};
        const npcBranches: Record<string, {
          branch: string;
          torch: boolean;
          awareness: string;
          checkedSpots: number;
        }> = {};
        for (const [id, e] of Object.entries(this.currState.entities)) {
          fears[id] = e.fear;
          if (id === 'e0') continue; // player entity has no AIController
          const ctrl = getAiController(id);
          if (!ctrl) continue;
          const agentRef = (ctrl as unknown as {
            agent: {
              winningBranch?: string;
              checkedHidingSpotIds?: Set<string>;
            };
          }).agent;
          npcBranches[id] = {
            branch: agentRef?.winningBranch ?? '?',
            torch: e.torchOn,
            awareness: this.latestAwareness.get(id) ?? 'unaware',
            checkedSpots: agentRef?.checkedHidingSpotIds?.size ?? 0,
          };
        }
        renderBus.emit('ai-debug', {
          blackboardHunter: this.currState.teamBlackboards.hunter.entries,
          blackboardHider: this.currState.teamBlackboards.hider.entries,
          fears,
          tick: this.currState.tick,
          npcBranches,
        });
      }
    }

    // P3 Plan 03-07: sync per-entity sprites. Player 'e0' sprite is already
    // handled by the legacy interpolation path below (camera follow); other
    // entities get a simple position/facing update straight from the sim
    // state (no interpolation in P3 — Plan 4 polish adds per-entity lerp if
    // it reads rough).
    for (const [id, entity] of Object.entries(this.currState.entities)) {
      if (id === 'e0') continue; // player sprite handled below with interpolation
      let sprite = this.sprites[id];
      if (!sprite) {
        // Tint by role so hunters/hiders/dogs/adults are visually distinguishable
        // during UAT (Test 2 follow-up). Entity.role is the authoritative source —
        // round-setup-confirmed may flip hunter↔hider but lazy-sprite creation
        // happens once per entity at first-sync so we use current role at that
        // moment. If the role flips later (team-switch), the sprite colour
        // remains — acceptable for UAT; a future pass can re-tint on role change.
        // Phase 09-04: thread entity id as 5th arg for per-entity bus subscriptions.
        sprite = new CharacterSprite(this, entity.position.x, entity.position.y, entity.role, id);
        sprite.setDepth(DEPTH_CHARACTER);
        this.sprites[id] = sprite;
      }
      sprite.setPosition(entity.position.x, entity.position.y);
      sprite.setRotation(-entity.facing); // LOCKED sign — do NOT change
      // Phase 09-04: drive animation state machine each frame.
      // isMoving: compare per-sim-tick positions (prevState vs currState); >0.5 px = moving.
      {
        const prevEnt = this.prevState.entities[id];
        const currEnt = this.currState.entities[id];
        const isMoving =
          prevEnt !== undefined && currEnt !== undefined
            ? Math.hypot(
                currEnt.position.x - prevEnt.position.x,
                currEnt.position.y - prevEnt.position.y,
              ) > 0.5
            : false;
        sprite.updateAnimState(entity, isMoving, this._sprintState[id] ?? false);
      }
    }

    // P4 Plan 04-04 (D-11, T-04-11): sync awareness telegraph sprites to follow
    // their character sprites at a -24 px y-offset. One Image per entity; setPosition
    // only — no allocation per frame (T-04-11 DoS guard).
    for (const [id, awareImg] of Object.entries(this.awarenessSprites)) {
      const charSprite = this.sprites[id];
      if (charSprite) {
        awareImg.setPosition(charSprite.x, charSprite.y - 24);
      }
    }

    // Interpolation — smooth motion despite 30 Hz sim.
    const alpha = this.accumulator / TICK_MS;
    const prev = this.prevState.entities['e0'];
    const curr = this.currState.entities['e0'];
    if (prev && curr) {
      const pos = lerp(prev.position, curr.position, alpha);
      // Plan 06-07 D-13: apply the FearFeedbackLayer-driven shiver delta on
      // top of the interpolated sim position. The sim's authoritative
      // entity.position stays clean — only the rendered sprite picks up the
      // 0..2 px shiver (P6-Pitfall E render-only mitigation hook).
      this.character.setPosition(
        pos.x + this.playerShiverDelta.x,
        pos.y + this.playerShiverDelta.y,
      );
      // Sim→Phaser facing-seam sign convention — LOCKED by
      // src/render/sprites/__tests__/character-sprite.test.ts (Plan 01-06).
      // Do NOT flip this sign without re-running that test RED→GREEN.
      //
      // nipplejs angle.radian is CCW-positive, y-UP (math convention):
      //   0=RIGHT, π/2=UP, π=LEFT, 3π/2=DOWN — confirmed against
      //   node_modules/nipplejs/src/Joystick.ts L405-408 humanize transform.
      // Phaser Container.rotation is CW-positive, y-DOWN (screen convention):
      //   0=RIGHT, π/2=DOWN, π=LEFT, -π/2=UP — confirmed against
      //   node_modules/phaser/types/Phaser.d.ts L14184 typedef.
      // Therefore phaserRotation = -simFacing.
      this.character.setRotation(-curr.facing); // LOCKED sign — do NOT change
      // Phase 09-04: drive player animation state machine each frame.
      // isMoving: compare per-sim-tick positions (prev vs curr); >0.5 px = moving.
      {
        const isMoving =
          Math.hypot(
            curr.position.x - prev.position.x,
            curr.position.y - prev.position.y,
          ) > 0.5;
        this.character.updateAnimState(curr, isMoving, this._sprintState['e0'] ?? false);
      }
      // Lookahead offset follows current facing — camera LEADS player (MOVE-03).
      // Phaser typedef: setFollowOffset values are SUBTRACTED from target
      // position. offset = (-cos(facing), -sin(facing)) * LOOKAHEAD places
      // the camera centre LOOKAHEAD pixels in the +facing direction from the
      // player, so the road ahead is visible. Signs match the wedge-rotation
      // seam above — if one flips, audit both.
      this.cameras.main.setFollowOffset(
        -Math.cos(curr.facing) * CAMERA_LOOKAHEAD,
        -Math.sin(curr.facing) * CAMERA_LOOKAHEAD,
      );
    }

    // Phase 08 Plan 04 (D-09/D-10, UI-SPEC §Player Proximity Check + §Tween Spec):
    // per-group roof-overhang proximity fade. Runs at render cadence (60 Hz).
    // Reads this.character.x/.y — the interpolated render-space position — NEVER
    // sim entity state. nextFadeState encodes the Re-trigger Guard (kill existing
    // tween before re-triggering); Tile is not a TweenTarget so we tween a plain
    // tweenCounter and apply via tile.setAlpha in onUpdate (STATE.md locked pattern).
    // No sim coupling: extractMapData is unchanged, tick() is unaware of fade state.
    {
      const px = this.character.x;
      const py = this.character.y;
      for (const group of this.fadeGroups) {
        const inside =
          px >= group.box.left &&
          px <= group.box.right &&
          py >= group.box.top &&
          py <= group.box.bottom;
        const decision = nextFadeState(group.state, inside);
        if (decision.action === 'start-in') {
          // Fade to 0.3 alpha in 150 ms (player enters bounding box — D-09).
          this.tweens.killTweensOf(group.tweenCounter);
          group.state = decision.state; // 'fading-in'
          this.tweens.add({
            targets: group.tweenCounter,
            alpha: 0.3,
            duration: 150,
            ease: 'Linear',
            onUpdate: () => {
              for (const tile of group.tiles) tile.setAlpha(group.tweenCounter.alpha);
            },
            onComplete: () => {
              group.state = 'transparent';
            },
          });
        } else if (decision.action === 'start-out') {
          // Restore to 1.0 alpha in 400 ms (player exits bounding box — D-09).
          this.tweens.killTweensOf(group.tweenCounter);
          group.state = decision.state; // 'fading-out'
          this.tweens.add({
            targets: group.tweenCounter,
            alpha: 1.0,
            duration: 400,
            ease: 'Linear',
            onUpdate: () => {
              for (const tile of group.tiles) tile.setAlpha(group.tweenCounter.alpha);
            },
            onComplete: () => {
              group.state = 'visible';
            },
          });
        } else {
          group.state = decision.state; // 'none' — no state change needed
        }
      }
    }

    // Phase 08.1 Item 1: per-tree polygon canopy fade. Runs at render cadence.
    // Uses point-in-ellipse (cheaper than point-in-polygon) for the proximity
    // test. Same nextFadeState machine + tween timings as tile-based fade above.
    // tweenCounter.alpha is applied to the Graphics object via setAlpha so each
    // tree fades independently. No tile manipulation — Graphics alpha only.
    {
      const px = this.character.x;
      const py = this.character.y;
      for (const group of this.polygonCanopyGroups) {
        const { tree } = group;
        const inside = isInsideEllipse(px, py, tree.cx, tree.cy, tree.rx, tree.ry);
        const decision = nextFadeState(group.state, inside);
        if (decision.action === 'start-in') {
          this.tweens.killTweensOf(group.tweenCounter);
          group.state = decision.state; // 'fading-in'
          this.tweens.add({
            targets: group.tweenCounter,
            alpha: CANOPY_FADE_ALPHA,
            duration: 150,
            ease: 'Linear',
            onUpdate: () => {
              group.graphics.setAlpha(group.tweenCounter.alpha);
            },
            onComplete: () => {
              group.state = 'transparent';
            },
          });
        } else if (decision.action === 'start-out') {
          this.tweens.killTweensOf(group.tweenCounter);
          group.state = decision.state; // 'fading-out'
          this.tweens.add({
            targets: group.tweenCounter,
            alpha: 1.0,
            duration: 400,
            ease: 'Linear',
            onUpdate: () => {
              group.graphics.setAlpha(group.tweenCounter.alpha);
            },
            onComplete: () => {
              group.state = 'visible';
            },
          });
        } else {
          group.state = decision.state; // 'none' — no tween needed
        }
      }
    }

    // Phase 08.1 Item 1 (fix): per-roof polygon walk-under fade. Runs at render
    // cadence (60 Hz). Uses point-in-polygon (exact shape) instead of bounding-box
    // proximity — roofs can be non-convex, so the exact test prevents premature
    // fading in buildings with re-entrant corners (e.g. L-shaped roofs).
    // Same nextFadeState machine + tween timings (150 ms fade-in, 400 ms fade-out)
    // as polygon canopies above. Only alpha is tweened; the Graphics are static.
    {
      const px = this.character.x;
      const py = this.character.y;
      for (const group of this.polygonRoofGroups) {
        const inside = pointInPolygon(px, py, group.vertices);
        const decision = nextFadeState(group.state, inside);
        if (decision.action === 'start-in') {
          this.tweens.killTweensOf(group.tweenCounter);
          group.state = decision.state; // 'fading-in'
          this.tweens.add({
            targets: group.tweenCounter,
            alpha: CANOPY_FADE_ALPHA,
            duration: 150,
            ease: 'Linear',
            onUpdate: () => {
              group.graphics.setAlpha(group.tweenCounter.alpha);
            },
            onComplete: () => {
              group.state = 'transparent';
            },
          });
        } else if (decision.action === 'start-out') {
          this.tweens.killTweensOf(group.tweenCounter);
          group.state = decision.state; // 'fading-out'
          this.tweens.add({
            targets: group.tweenCounter,
            alpha: 1.0,
            duration: 400,
            ease: 'Linear',
            onUpdate: () => {
              group.graphics.setAlpha(group.tweenCounter.alpha);
            },
            onComplete: () => {
              group.state = 'visible';
            },
          });
        } else {
          group.state = decision.state; // 'none' — no tween needed
        }
      }
    }

    // P2 Plan 02-03: redraw the light mask at render cadence. LightMask.redraw
    // is gated by sim tick internally (Pitfall 5) so 60 Hz render on a 30 Hz
    // sim produces one fill+stamp batch per TWO frames on average.
    this.drawLightMask();

    // P3 Plan 03-07: redraw the foxguard debug ring at render cadence.
    // Ships in production but gated at runtime by isDebugEnabled() so the
    // ring is only drawn when the debug flag is active.
    if (isDebugEnabled()) {
      this.drawFoxguardDebugRing();
    }

    renderBus.emit('render-fps', { fps: Math.round(1000 / Math.max(delta, 1)) });
  }

  /**
   * P3 Plan 03-08 (WARNING-15 tier isolation): resolve a character profile's
   * display name render-side so the sim-tick payload carries a string and UI
   * components never import PROFILES. Shared by the player ('e0') resolution and
   * the P7 spectator-watching name. Admin CMS (CHAR-07) swaps the resolution
   * source without changing callers.
   */
  private resolveDisplayName(characterId: string): string {
    const prof = hasProfile(characterId)
      ? getProfile(characterId)
      : getProfile(DEFAULT_CHARACTER_ID);
    return prof.id.charAt(0).toUpperCase() + prof.id.slice(1);
  }

  /**
   * P7 Plan 07-07 (UI-05 / D-08): spectator camera. Pure render — reads sim
   * state to choose a follow target, never writes back; golden hash untouched.
   *
   * When the local player ('e0') is caught (or spectating), the camera follows a
   * FREE hider instead of the dead player sprite:
   *   - candidates = id-SORTED free hiders (excluding e0). Sort makes the choice
   *     deterministic (07-UI-SPEC).
   *   - first entry into spectating, OR auto-fallback when the watched hider is
   *     no longer free (Pitfall C — caught mid-watch), targets candidates[0].
   *   - an advance gesture cycles round-robin to the next candidate.
   * When the player is NOT caught, the camera returns to following e0 (only
   * re-asserted once, on the transition out of spectating).
   *
   * Called once per sim tick from update() right before the sim-tick emit so the
   * `spectatorWatching` field is fresh for the SpectatorBanner.
   */
  private updateSpectatorCamera(): void {
    const player = this.currState.entities['e0'];
    const spectating =
      player?.status === 'caught' || player?.status === 'spectating';

    if (!spectating) {
      // Back in play (or never caught): re-assert the e0 follow exactly once on
      // the transition so we don't fight the per-frame interpolation follow.
      if (this.watchedHiderId !== null) {
        this.watchedHiderId = null;
        this.followSprite('e0');
      }
      return;
    }

    // Build the id-sorted free-hider candidate set (exclude the caught player).
    const freeIds = Object.values(this.currState.entities)
      .filter(
        (e) =>
          e.id !== 'e0' &&
          e.role === 'hider' &&
          e.status !== 'caught' &&
          e.status !== 'spectating',
      )
      .map((e) => e.id)
      .sort();

    if (freeIds.length === 0) {
      // No one left to watch (round effectively over) — hold the last camera.
      this.watchedHiderId = null;
      return;
    }

    let idx = this.watchedHiderId ? freeIds.indexOf(this.watchedHiderId) : -1;
    if (this.spectatorAdvanceRequested) {
      this.spectatorAdvanceRequested = false;
      idx = idx < 0 ? 0 : (idx + 1) % freeIds.length;
    } else if (idx < 0) {
      // First watch OR auto-fallback: the previously watched hider dropped out
      // of the free set, so snap to the lowest-id free hider.
      idx = 0;
    }

    const nextId = freeIds[idx]!;
    if (nextId !== this.watchedHiderId) {
      this.watchedHiderId = nextId;
      this.followSprite(nextId);
    }
  }

  /**
   * P7 Plan 07-07: point the main camera at a sprite by entity id. Honours
   * reduced-motion (lerp 1,1 = instant cut) and no-ops if the sprite has not
   * spawned yet (boot window). Mirrors the team-switch startFollow swap idiom.
   */
  private followSprite(id: string): void {
    const sprite = this.sprites[id];
    if (!sprite) return;
    const lerp = this.reducedMotion ? 1 : CAMERA_LERP;
    this.cameras.main.startFollow(sprite, true, lerp, lerp);
  }

  /**
   * P3 Plan 03-07 D-32 + UI-SPEC §Diegetic Team-Switch Contract.
   *
   * Three-beat camera pan played when the round-FSM fires `team-switched`:
   *   - 2000 ms Sine.easeInOut pan to plinth centre.
   *   - 3000 ms hold while sim lerps entities (`applyTeamSwitchLerp`) to their
   *     new-round spawn positions.
   *   - 2000 ms Sine.easeInOut pan to player's NEW spawn, then resume follow.
   *
   * Sprites are NOT tweened here — sim owns entity motion during the 210-tick
   * team-switch window (Pitfall 3 avoidance: no two sources of truth for entity
   * position).
   */
  private startTeamSwitchAnimation(plinthCentre: Vec2, playerNewSpawn: Vec2): void {
    this.cameras.main.stopFollow();
    this.cameras.main.pan(
      plinthCentre.x,
      plinthCentre.y,
      TEAM_SWITCH_PAN_TO_PLINTH_MS,
      'Sine.easeInOut',
      true,
    );
    this.time.delayedCall(TEAM_SWITCH_PLAYER_PAN_DELAY_MS, () => {
      this.cameras.main.pan(
        playerNewSpawn.x,
        playerNewSpawn.y,
        TEAM_SWITCH_PAN_TO_PLAYER_MS,
        'Sine.easeInOut',
        true,
        (_cam: Phaser.Cameras.Scene2D.Camera, progress: number) => {
          if (progress === 1) {
            this.cameras.main.startFollow(
              this.sprites['e0']!,
              true,
              CAMERA_LERP,
              CAMERA_LERP,
            );
          }
        },
      );
    });
  }

  /**
   * P3 Plan 03-07: draw a small ring around each hunter that has an active
   * foxguard streak. Colour ramps green → amber → coral as the streak
   * approaches grace (180 ticks). Re-created fresh each frame so cleared
   * streaks don't leave a stale ring behind. Called only when
   * isDebugEnabled() returns true (runtime debug flag).
   */
  private drawFoxguardDebugRing(): void {
    if (!this.foxguardDebugRing) {
      this.foxguardDebugRing = this.add.graphics();
      this.foxguardDebugRing.setDepth(DEPTH_HUD_HIGHLIGHT);
    }
    const g = this.foxguardDebugRing;
    g.clear();
    const currentTick = this.currState.tick;
    for (const entity of Object.values(this.currState.entities)) {
      if (entity.role !== 'hunter') continue;
      const streakStart = entity.foxguardConditionsStreakStartTick;
      if (streakStart === null) continue;
      const streakLen = currentTick - streakStart;
      let color: number;
      if (streakLen < 60) color = 0x5cb874;      // green
      else if (streakLen < 120) color = 0xe8a838; // amber
      else color = 0xff7f66;                      // coral
      g.lineStyle(2, color, 0.9);
      g.strokeCircle(entity.position.x, entity.position.y, 24);
    }
  }

  /**
   * P4 Plan 04-04 (D-11): spawn, swap, or remove the awareness telegraph Image
   * for a given entity.
   *
   * - level !== 'unaware' → create Image if absent (at charSprite.position - 24 py)
   *   or setTexture if already present. One Image per entity (T-04-11 reuse).
   * - level === 'unaware' → destroy + delete the Image.
   *
   * Texture key map:
   *   suspicious → 'telegraph-suspicious' (yellow '?')
   *   alerted    → 'telegraph-alerted'    (orange '!')
   *   spotted    → 'telegraph-spotted'    (red '!')
   *
   * Called from forwardSimEvents 'awareness-changed' case. Visibility follows
   * character sprite each frame via the awarenessSprites position-sync loop in update().
   */
  private updateAwarenessSprite(
    entityId: string,
    level: 'unaware' | 'suspicious' | 'alerted' | 'spotted',
  ): void {
    const existing = this.awarenessSprites[entityId];
    if (level === 'unaware') {
      if (existing) {
        existing.destroy();
        delete this.awarenessSprites[entityId];
      }
      return;
    }
    const key =
      level === 'suspicious'
        ? 'telegraph-suspicious'
        : level === 'alerted'
        ? 'telegraph-alerted'
        : 'telegraph-spotted';
    if (!existing) {
      const charSprite = this.sprites[entityId];
      if (!charSprite) return; // defensive: awareness may arrive before sprite is spawned
      const img = this.add.image(charSprite.x, charSprite.y - 24, key);
      img.setDepth(DEPTH_HUD_HIGHLIGHT);
      this.awarenessSprites[entityId] = img;
    } else {
      existing.setTexture(key);
    }
  }

  /**
   * P2 Plan 02-03: redraw the LightMask from current sim state.
   *
   * Reads (never writes) WorldState.playerFov + .lightsState + MapData.lights
   * per D-17 one-way contract. Motion-light fade alpha is interpolated here
   * using `state.tick - fadingTick` against P2_MOTION_LIGHT_FADE_TICKS — that
   * computation belongs in the render-side mask (not sim) because fade is a
   * visual-only concern; sim only tracks WHEN the fade started.
   */
  private drawLightMask(): void {
    if (!this.cachedMapData || !this.lightMask) return;
    const state = this.currState;
    const tileW = this.cachedMapData.tileWidth;
    const mapLights = this.cachedMapData.lights;

    const permanentLights: LightInstance[] = [];
    const motionLights: LightInstance[] = [];
    // Phase 08.1 Item 3: torchCones is declared here (before the mapLights loop)
    // so static authored cone lights can be pushed in the same loop that classifies
    // permanent/motion lights. Dynamic hunter/player cones are appended below.
    const torchCones: TorchParams[] = [];

    for (const L of mapLights) {
      const radiusPx = L.radius * tileW;
      if (L.type === 'permanent') {
        // Phase 08.1 Item 3: permanent lights with kind='cone' are rendered as
        // directional torch-cone sprites (static, not dynamic like hunter torches).
        // Convert Tiled screen-convention angleDeg (0=right, 90=down) to sim
        // CCW-radians by negating: simFacing = -(angleDeg × π/180).
        if (L.kind === 'cone') {
          const facingRad = -(L.angleDeg ?? 0) * (Math.PI / 180);
          torchCones.push({
            x: L.position.x,
            y: L.position.y,
            facingRad,
            isPlayer: false,
            colorOverride: L.color,
          });
        } else {
          // kind='omni' (or absent) → radial glow at authored radiusPx.
          permanentLights.push({
            x: L.position.x,
            y: L.position.y,
            alpha: L.intensity,
            radiusPx,
          });
        }
        continue;
      }
      // Motion light — activation/fade state lives in WorldState.lightsState.
      const s = state.lightsState[L.id];
      if (!s) continue; // never activated OR already faded-off (deleted from state)
      let alpha = L.intensity;
      if (s.fadingTick !== null) {
        const fadeProgress = (state.tick - s.fadingTick) / P2_MOTION_LIGHT_FADE_TICKS;
        alpha = L.intensity * Math.max(0, 1 - fadeProgress);
        if (alpha <= 0) continue;
      }
      motionLights.push({
        x: L.position.x,
        y: L.position.y,
        alpha,
        radiusPx,
      });
    }

    // P7 Plan 07-07 (D-11, folded render todo): append dynamic hunter torch cones.
    // Phase 08.1 Item 3: static cone lights were prepended above (during the
    // mapLights loop). Dynamic cones come here — hunter + player, sorted by id
    // for deterministic stamp order (T-07-15). Static cones stamp first (map order),
    // dynamic cones stamp second (sorted); both ADD-blended so order is visual-only.
    //   - Player ('e0'): keeps the pre-P7 gate (torchOn AND playerFov computed).
    //     Untinted (isPlayer:true) so the on-device look is unchanged.
    //   - AI hunters: every hunter (not e0) with torchOn gets a tinted cone so a
    //     hider/spectator can read sweeping danger.
    for (const id of Object.keys(state.entities).sort()) {
      const e = state.entities[id]!;
      if (!e.torchOn) continue;
      if (id === 'e0') {
        if (state.playerFov === null) continue; // player cone needs FOV computed
        torchCones.push({
          x: e.position.x,
          y: e.position.y,
          facingRad: e.facing,
          isPlayer: true,
        });
      } else if (e.role === 'hunter') {
        torchCones.push({
          x: e.position.x,
          y: e.position.y,
          facingRad: e.facing,
          isPlayer: false,
        });
      }
    }

    this.lightMask.redraw({
      darkTint: DARKNESS_FILL_HEX,
      permanentLights,
      motionLights,
      torchCones,
      tick: state.tick,
    });
  }

  /**
   * P2 Plan 02-03 (UI-SPEC §DevOverlay P2 extension):
   * Emit a synthetic `lighting-debug` snapshot once per sim tick.
   *
   * DevOverlay subscribes to this on the renderBus and renders the Torch + FOV
   * + Lights rows without needing to peek at WorldState (render ↔ ui coupling
   * is bus-only, same as P0). Plan 02-04 wires the DevOverlay subscription.
   */
  private emitLightingDebug(): void {
    const state = this.currState;
    const player = state.entities['e0'];
    const torchOn = player?.torchOn === true;
    const fovTileCount = state.playerFov
      ? countFovBits(state.playerFov.tiles)
      : 0;
    const motionLights = buildMotionLightDebugList(
      state.lightsState,
      this.cachedMapData?.lights ?? [],
      state.tick,
    );
    // P2 Plan 02-07 (UAT Gap 2): playerPosition drives the DevOverlay Pos row.
    // World is pixels (entity.position is always px post-Phase-1). Tile is
    // floor(world / tileWidth); null when cachedMapData isn't ready yet
    // (pre-create() window) so DevOverlay renders "—" for tiles instead of 0,0.
    const worldPos = player?.position ?? { x: 0, y: 0 };
    const tileW = this.cachedMapData?.tileWidth;
    const tileH = this.cachedMapData?.tileHeight;
    const tilePos =
      tileW !== undefined && tileH !== undefined
        ? { x: Math.floor(worldPos.x / tileW), y: Math.floor(worldPos.y / tileH) }
        : null;
    renderBus.emit('lighting-debug', {
      torchOn,
      fovTileCount,
      motionLights,
      tick: state.tick,
      playerPosition: { world: worldPos, tile: tilePos },
    });
  }

  /** A* smoke test (MAP-13). Picks pool.terrace → threshing-floor if both exist,
   *  else first-zone → last-zone of the zones map. Emits a 'smoke-test' event. */
  private runSmokeTest(mapData: ReturnType<typeof extractMapData>): void {
    const zoneNames = Array.from(mapData.zones.keys());
    if (zoneNames.length < 2) {
      renderBus.emit('smoke-test', { ok: false, pathLength: 0, from: 'none', to: 'none' });
      return;
    }
    const fromName = mapData.zones.has('pool.terrace') ? 'pool.terrace' : zoneNames[0]!;
    const toName = mapData.zones.has('threshing-floor') ? 'threshing-floor' : zoneNames[zoneNames.length - 1]!;
    const fromZone = mapData.zones.get(fromName)!;
    const toZone = mapData.zones.get(toName)!;
    const fromCentre = zoneCenter(fromZone);
    const toCentre = zoneCenter(toZone);
    const fromTile = {
      x: Math.floor(fromCentre.x / mapData.tileWidth),
      y: Math.floor(fromCentre.y / mapData.tileHeight),
    };
    const toTile = {
      x: Math.floor(toCentre.x / mapData.tileWidth),
      y: Math.floor(toCentre.y / mapData.tileHeight),
    };
    const path = findTilePath(fromTile.x, fromTile.y, toTile.x, toTile.y, mapData.grid);
    const ok = path.length > 0;
    renderBus.emit('smoke-test', { ok, pathLength: path.length, from: fromName, to: toName });

    // If the smoke test fails on the real map, surface via /api/log (P0 TEL-01).
    // On the placeholder (tiny map), a failure is expected only if the three
    // placeholder zones happen to be unreachable — which they aren't.
    if (!ok) {
      try {
        const blob = new Blob(
          [JSON.stringify({ type: 'smoke-test-failure', from: fromName, to: toName, pathLength: 0 })],
          { type: 'application/json' },
        );
        navigator.sendBeacon?.('/api/log', blob);
      } catch {
        // Fail-open — telemetry must never break the game.
      }
    }
  }

  /**
   * Phase 08.1 — draw authored surface polygons into a static Graphics object at
   * DEPTH_FLOOR (0). Called ONCE from create(); the Graphics retain their commands
   * for the lifetime of the scene (no per-frame redraw needed — map layout is fixed).
   *
   * Surface polygons: each is filled with its surface-type colour from
   * surface-types.json. Array order is preserved (later polygons drawn on top of
   * earlier ones) which matches the layer z-order in Tiled.
   *
   * Roof polygons are handled separately by buildPolygonRoofs() — they render at
   * DEPTH_CANOPY (30) above characters with walk-under proximity fade.
   *
   * Null-safe: empty array → no Graphics created (no visible draw).
   */
  private drawSurfaces(surfaces: SurfacePolygon[]): void {
    if (surfaces.length === 0) return;
    // Use the path API (beginPath/moveTo/lineTo/closePath/fillPath) which accepts
    // raw x/y numbers and avoids the Phaser.Math.Vector2 vs Vector2Like type gap.
    const g = this.add.graphics();
    g.setDepth(DEPTH_FLOOR);
    for (const surface of surfaces) {
      if (surface.vertices.length < 3) continue;
      g.fillStyle(surface.color, 1.0);
      g.beginPath();
      const first = surface.vertices[0]!;
      g.moveTo(first.x, first.y);
      for (let i = 1; i < surface.vertices.length; i++) {
        const v = surface.vertices[i]!;
        g.lineTo(v.x, v.y);
      }
      g.closePath();
      g.fillPath();
    }
  }

  /**
   * Phase 08.1 Item 2 — draw obstacles (filled polygons) and fences (thin lines)
   * as static Graphics at DEPTH_ROOF (8): above ground, below characters.
   *
   * Colour: muted stone/wood #8a7a66 — distinct from terracotta roofs (#b5633a)
   * and from the warm amber palette, reads as a physical obstacle on the warm map.
   *
   * Obstacles: solid fill. Fences: 2 px stroked polyline (no fill — a fence is
   * see-through but body-blocking). Both use the path API for concave support.
   *
   * Null-safe: empty arrays → no Graphics created. Called once from create().
   */
  private drawObstaclesAndFences(
    obstacles: ObstaclePolygon[],
    fences: FencePolyline[],
  ): void {
    const OBSTACLE_COLOR = 0x8a7a66;
    if (obstacles.length > 0 || fences.length > 0) {
      const g = this.add.graphics();
      g.setDepth(DEPTH_ROOF);
      // Draw obstacle polygons as solid fills.
      if (obstacles.length > 0) {
        g.fillStyle(OBSTACLE_COLOR, 0.9);
        for (const obs of obstacles) {
          if (obs.vertices.length < 3) continue;
          g.beginPath();
          const first = obs.vertices[0]!;
          g.moveTo(first.x, first.y);
          for (let i = 1; i < obs.vertices.length; i++) {
            const v = obs.vertices[i]!;
            g.lineTo(v.x, v.y);
          }
          g.closePath();
          g.fillPath();
        }
      }
      // Draw fence polylines as 2 px stroked open paths.
      if (fences.length > 0) {
        g.lineStyle(2, OBSTACLE_COLOR, 0.85);
        for (const fence of fences) {
          if (fence.points.length < 2) continue;
          g.beginPath();
          const first = fence.points[0]!;
          g.moveTo(first.x, first.y);
          for (let i = 1; i < fence.points.length; i++) {
            const p = fence.points[i]!;
            g.lineTo(p.x, p.y);
          }
          // strokePath (not closePath) — fences are open polylines.
          (g as unknown as { strokePath: () => void }).strokePath();
        }
      }
    }
  }

  /**
   * Phase 08.1 Item 1 (fix) — build per-roof polygon Graphics at DEPTH_CANOPY (30)
   * with walk-under proximity fade. Called ONCE from create().
   *
   * Each roof polygon gets its own Graphics object drawn here (terracotta #b5633a).
   * In update(), pointInPolygon is used to test whether the player is inside the
   * polygon; if so, the alpha tweens to CANOPY_FADE_ALPHA (0.3) over 150 ms
   * (restores over 400 ms on exit) — identical timing to tree canopy fades.
   *
   * This replaces the now-hidden roof-overhang tile layer: roof polygons are crisp
   * (no blocky eave tiles), render ABOVE characters, and fade on player proximity.
   * The resulting PolygonRoofGroup entries are stored in polygonRoofGroups for
   * the per-frame fade loop in update().
   *
   * Null-safe: empty array → no Graphics created. No per-frame overhead.
   */
  private buildPolygonRoofs(roofs: RoofPolygon[]): void {
    const TERRACOTTA = 0xb5633a;
    for (const roof of roofs) {
      if (roof.vertices.length < 3) continue;

      const g = this.add.graphics() as unknown as Phaser.GameObjects.Graphics & {
        setAlpha: (a: number) => void;
      };
      g.setDepth(DEPTH_CANOPY);
      g.fillStyle(TERRACOTTA, 1.0);
      g.beginPath();
      const first = roof.vertices[0]!;
      g.moveTo(first.x, first.y);
      for (let i = 1; i < roof.vertices.length; i++) {
        const v = roof.vertices[i]!;
        g.lineTo(v.x, v.y);
      }
      g.closePath();
      g.fillPath();

      this.polygonRoofGroups.push({
        graphics: g,
        vertices: roof.vertices,
        tweenCounter: { alpha: 1 },
        state: 'visible',
      });
    }
  }

  /**
   * Phase 08.1 Item 2 — draw tree trunks as filled ellipses at DEPTH_TRUNK (12).
   *
   * Trunk size = 22% of the canopy semi-axes (min 3 px) — the SAME formula used
   * by `extractTrees` in tilemap-loader.ts to populate `trunkRx`/`trunkRy` on
   * each `TreeObject`. Both this render and the sim collision helper
   * `circleOverlapsTrunk` in movement.ts use the pre-computed trunk fields so
   * the visual footprint and the collision shape are always identical.
   *
   * Depth: DEPTH_TRUNK (12) — above ground surfaces and obstacle props (8),
   * below the light mask (15) and characters (20). The player walks visually
   * IN FRONT of a trunk (character sprite above trunk sprite). The canopy blob
   * renders at DEPTH_CANOPY (30) above characters, unchanged.
   *
   * Colour: #5a4a38 (warm dark brown) — clearly visible on the scrub ground
   * (#c4a878) and distinct from the terracotta building fills (#b5633a).
   *
   * All 273 trunks are drawn into a SINGLE static Graphics object in create().
   * No per-frame redraw. Phaser 4 retains Graphics commands in the WebGL buffer.
   * Null-safe: empty array → no Graphics created.
   */
  private buildTreeTrunks(trees: TreeObject[]): void {
    if (trees.length === 0) return;
    const TRUNK_COLOR = 0x5a4a38; // warm dark brown
    const g = this.add.graphics();
    g.setDepth(DEPTH_TRUNK);
    g.fillStyle(TRUNK_COLOR, 1.0);
    for (const tree of trees) {
      const { cx, cy, trunkRx, trunkRy } = tree;
      if (trunkRx <= 0 || trunkRy <= 0) continue;
      // Phaser Graphics.fillEllipse(x, y, width, height) where x,y = centre.
      // width = 2 * trunkRx, height = 2 * trunkRy.
      (g as unknown as { fillEllipse: (x: number, y: number, w: number, h: number) => void })
        .fillEllipse(cx, cy, trunkRx * 2, trunkRy * 2);
    }
  }

  /**
   * Phase 08.1 Item 1 — build per-tree polygon canopy Graphics at DEPTH_CANOPY.
   *
   * Each tree gets its own `Phaser.GameObjects.Graphics` object drawn ONCE here.
   * The shape is an irregular "natural blob" polygon generated from the tree's
   * ellipse (cx, cy, rx, ry) using alea seeded by the tree's pixel centre so
   * every tree's shape is:
   *   - STABLE across page reloads (same seed → same shape), and
   *   - UNIQUE between trees (different seed → different shape).
   *
   * numPoints is deterministically chosen from the seeded RNG (10–14 range)
   * so tree sizes also vary between trees in a stable way.
   *
   * Performance note: 273 trees, all drawn in create() with no per-frame
   * overhead. The Graphics objects remain static (draw commands baked into
   * the WebGL command buffer); only alpha is tweened per proximity.
   *
   * Called from create() after drawSurfacePolygons(). The resulting groups
   * are stored in `polygonCanopyGroups` for the per-frame fade loop in update().
   */
  private buildPolygonCanopies(trees: TreeObject[]): void {
    for (const tree of trees) {
      const { cx, cy, rx, ry } = tree;
      // Seed the RNG from the tree's pixel centre (rounded to avoid float
      // precision drift). Same position → same seed → same shape on every load.
      const rng = Alea(`${Math.round(cx)},${Math.round(cy)}`);
      // Choose numPoints from the seeded RNG so small/large trees feel different.
      const numPoints =
        CANOPY_MIN_POINTS + Math.floor(rng() * (CANOPY_MAX_POINTS - CANOPY_MIN_POINTS + 1));
      const vertices = generateBlobVertices(cx, cy, rx, ry, numPoints, rng);
      if (vertices.length < 3) continue;

      const g = this.add.graphics() as unknown as Phaser.GameObjects.Graphics & {
        setAlpha: (a: number) => void;
      };
      g.setDepth(DEPTH_CANOPY);
      g.fillStyle(CANOPY_COLOR, 1.0);
      g.beginPath();
      const first = vertices[0]!;
      g.moveTo(first.x, first.y);
      for (let i = 1; i < vertices.length; i++) {
        const v = vertices[i]!;
        g.lineTo(v.x, v.y);
      }
      g.closePath();
      g.fillPath();

      this.polygonCanopyGroups.push({
        graphics: g,
        tree,
        tweenCounter: { alpha: 1 },
        state: 'visible',
      });
    }
  }
}

// -- Module-local helpers -----------------------------------------------------

/**
 * Forward sim events emitted by tick() into the render bus.
 *
 * P2 Plan 02-03 scope: only lighting-adjacent events (entity-moved is useful
 * for DevOverlay tile-count + future P4 perception correlation). Other future
 * sim events (catch, round-ended, etc.) land in later phases and extend this
 * switch. The render-side RenderEventMap carries identical shapes to the
 * sim-side GameEventMap so the payload passes through unchanged.
 *
 * Phase 6 (Plan 06-05) extension: 'entity-moved' is enriched at emit time
 * with status / characterId / sprint / position context so the audio
 * footsteps module can run its D-11 silent-hide short-circuit + quietness
 * gain + sprint multiplier without re-reading sim state from the render
 * tier (preserves the WARNING-15 tier-isolation contract).
 */
export function forwardSimEvents(
  events: readonly GameEvent[],
  currState: WorldState,
  prevState: WorldState,
  tileWidth: number,
): void {
  for (const ev of events) {
    switch (ev.type) {
      case 'entity-moved': {
        // P6 (Plan 06-05) — enrich with per-entity context for footstep
        // gain math. We read currState first (post-tick) and fall back to
        // prevState for the rare boot-window case where the entity has
        // since been removed (round transition tick).
        const entity = currState.entities[ev.entityId] ?? prevState.entities[ev.entityId];
        const characterId = entity?.characterId ?? '';
        const status = entity?.status ?? 'free';
        // Sprint detection (heuristic, render-only): per-tick distance vs
        // baseline. baseSpeedMultiplier ~ 1.0 at 30Hz → ~1.0 tile / 30
        // ticks. We flag sprint when the distance moved this tick exceeds
        // 1.2x of one tile-step's worth of baseline movement. Falls back
        // to false when tileWidth is zero or the entity is missing.
        const dx = ev.to.x - ev.from.x;
        const dy = ev.to.y - ev.from.y;
        const distPx = Math.hypot(dx, dy);
        // 30 Hz sim: one tile crossing per 30 ticks at base speed → ~1.07
        // px / tick at tileWidth=32. A sprint multiplier of 1.4 raises that
        // to ~1.5 px / tick, so a midpoint threshold of 1.3 px / tick is
        // a reasonable sprint flag. Volume is the only consumer so a small
        // amount of jitter is acceptable.
        const sprintThreshold = (tileWidth / 30) * 1.3;
        const sprint = distPx > sprintThreshold;
        renderBus.emit('entity-moved', {
          entityId: ev.entityId,
          from: ev.from,
          to: ev.to,
          tick: ev.tick,
          position: ev.to,
          characterId,
          status,
          sprint,
        });
        break;
      }
      case 'torch-flicked':
        renderBus.emit('torch-flicked', {
          entityId: ev.entityId,
          on: ev.on,
          tick: ev.tick,
        });
        break;
      case 'motion-light-on':
        renderBus.emit('motion-light-on', {
          lightId: ev.lightId,
          tick: ev.tick,
        });
        break;
      case 'motion-light-off':
        renderBus.emit('motion-light-off', {
          lightId: ev.lightId,
          tick: ev.tick,
        });
        break;
      // P3 Plan 03-07 — forward the 12 new sim event variants. Payload shape
      // is identical on sim (GameEventMap) + render (RenderEventMap) sides so
      // the narrow cast through is safe.
      case 'round-started':
        renderBus.emit('round-started', {
          roundNumber: ev.roundNumber,
          seed: ev.seed,
          tick: ev.tick,
        });
        break;
      case 'round-ended':
        renderBus.emit('round-ended', {
          roundNumber: ev.roundNumber,
          endReason: ev.endReason,
          caughtCount: ev.caughtCount,
          totalHiders: ev.totalHiders,
          tick: ev.tick,
        });
        break;
      case 'team-switched':
        renderBus.emit('team-switched', {
          roundNumber: ev.roundNumber,
          tick: ev.tick,
        });
        break;
      case 'catch':
        renderBus.emit('catch', {
          hunterId: ev.hunterId,
          hiderId: ev.hiderId,
          tick: ev.tick,
        });
        break;
      case 'hider-parked':
        renderBus.emit('hider-parked', {
          hunterId: ev.hunterId,
          hiderId: ev.hiderId,
          tick: ev.tick,
        });
        break;
      case 'hider-released':
        renderBus.emit('hider-released', {
          liberatorId: ev.liberatorId,
          freedIds: ev.freedIds,
          tick: ev.tick,
        });
        break;
      case 'foxguard-called':
        renderBus.emit('foxguard-called', {
          hunterId: ev.hunterId,
          shouterId: ev.shouterId,
          tick: ev.tick,
        });
        break;
      case 'hiding-entered':
        renderBus.emit('hiding-entered', {
          entityId: ev.entityId,
          spotId: ev.spotId,
          tick: ev.tick,
        });
        break;
      case 'hiding-exited':
        renderBus.emit('hiding-exited', {
          entityId: ev.entityId,
          spotId: ev.spotId,
          tick: ev.tick,
        });
        break;
      case 'peek-started':
        renderBus.emit('peek-started', {
          entityId: ev.entityId,
          tick: ev.tick,
        });
        break;
      case 'peek-ended':
        renderBus.emit('peek-ended', {
          entityId: ev.entityId,
          tick: ev.tick,
        });
        break;
      case 'stamina-depleted':
        renderBus.emit('stamina-depleted', {
          entityId: ev.entityId,
          tick: ev.tick,
        });
        break;
      // P4 Plan 04-04 (D-11): forward awareness-changed to renderBus AND update
      // the per-hunter telegraph sprite. Forwarding lets the DevOverlay + future
      // audio layer (P6) subscribe without reading sim state directly.
      case 'awareness-changed':
        renderBus.emit('awareness-changed', {
          entityId: ev.entityId,
          level: ev.level,
          tick: ev.tick,
        });
        break;
      // P4 Plan 04-10 (Task 3): 5 new P4 sim events forwarded to renderBus.
      // DevOverlay + P6 audio can subscribe without reading sim state.
      case 'shout':
        renderBus.emit('shout', {
          sourceId: ev.sourceId,
          position: ev.position,
          tick: ev.tick,
        });
        break;
      case 'fear-threshold-crossed':
        renderBus.emit('fear-threshold-crossed', {
          entityId: ev.entityId,
          threshold: ev.threshold,
          direction: ev.direction,
          tick: ev.tick,
        });
        break;
      case 'dog-barked':
        renderBus.emit('dog-barked', {
          dogId: ev.dogId,
          position: ev.position,
          tick: ev.tick,
        });
        break;
      case 'adult-commented':
        renderBus.emit('adult-commented', {
          adultId: ev.adultId,
          eventTag: ev.eventTag,
          lineIndex: ev.lineIndex,
          tick: ev.tick,
        });
        break;
      // P6 D-04 / CR-01 closure (Plan 06-09): the load-bearing dialogue
      // event. tick.ts emits this from 5 trigger sites (catch, hider-released,
      // foxguard-called, round-started, round-ended) via maybeEmitCharacterSpoke.
      // The waffle audio subscriber (manager.ts:163) and BubbleLayer flavour-tier
      // subscriber (BubbleLayer.tsx:174) both depend on this re-emit.
      case 'character-spoke':
        renderBus.emit('character-spoke', {
          entityId: ev.entityId,
          characterId: ev.characterId,
          eventTag: ev.eventTag,
          lineIndex: ev.lineIndex,
          tick: ev.tick,
        });
        break;
      default:
        break;
    }
  }
}

/**
 * Popcount a Uint32Array bitset using the Hamming-weight SWAR trick.
 *
 * Called once per sim tick (30 Hz), never per render frame. ~470 ints for a
 * 150x100 tile map → ~15k ops/s — imperceptible on any device in the family
 * pool. Kept as a module-local function (not a class method) so it can be
 * unit-tested in isolation without scene lifecycle setup.
 */
function countFovBits(tiles: Uint32Array): number {
  let n = 0;
  for (let i = 0; i < tiles.length; i++) {
    let w = tiles[i]! >>> 0;
    w = w - ((w >>> 1) & 0x55555555);
    w = (w & 0x33333333) + ((w >>> 2) & 0x33333333);
    w = (w + (w >>> 4)) & 0x0f0f0f0f;
    n += (w * 0x01010101) >>> 24;
  }
  return n;
}

/**
 * Classify each authored motion light's current state for the DevOverlay ring
 * renderer (UI-SPEC §DevOverlay P2 extension).
 *
 *   - fadingTick !== null          → 'fading' (coral ring)
 *   - tick - activatedTick <= 2    → 'armed' (soft green — freshly activated
 *                                   OR entity just re-entered the triggerRadius)
 *   - tick - activatedTick < STAY  → 'staying' (amber ring — 4 s hold)
 *   - else                         → 'staying' (the sim will transition to
 *                                   fading on the next tick; we render a
 *                                   continuous ring here)
 *
 * The 2-tick 'armed' threshold is intentional: sim emits motion-light-on
 * exactly once per off→on transition, and during re-arms (staying→re-arm) sim
 * updates activatedTick without a second event. The DevOverlay ring ANIMATES
 * green briefly on each rearm, so a small look-back window makes the visual
 * read correct regardless of whether the DevOverlay subscribed via the event
 * firehose or via the lighting-debug snapshot.
 *
 * P2_MOTION_LIGHT_FADE_TICKS is referenced so tsc/eslint do not flag it unused
 * if the classifier shape changes in future plans (Plan 02-04 DevOverlay uses
 * it to animate the ring fade alpha locally).
 */
function buildMotionLightDebugList(
  lightsState: LightsState,
  lights: ReadonlyArray<{ id: string; type: 'permanent' | 'motion' }>,
  currentTick: number,
): MotionLightDebugEntry[] {
  const out: MotionLightDebugEntry[] = [];
  for (const L of lights) {
    if (L.type !== 'motion') continue;
    const s = lightsState[L.id];
    if (!s) continue; // never activated OR already faded-off (deleted from state)
    let state: MotionLightDebugEntry['state'];
    if (s.fadingTick !== null) {
      state = 'fading';
    } else if (currentTick - s.activatedTick <= 2) {
      state = 'armed';
    } else if (currentTick - s.activatedTick < P2_MOTION_LIGHT_STAY_TICKS) {
      state = 'staying';
    } else {
      // Stay window elapsed but sim hasn't flipped fadingTick yet (transition
      // lands on the next sim tick). Render as 'staying' for continuity.
      state = 'staying';
    }
    out.push({ id: L.id, state, activatedTick: s.activatedTick });
  }
  // Referenced so the import is never tree-shaken by downstream tooling that
  // inspects the symbol graph. DevOverlay (Plan 02-04) re-imports this constant
  // to animate the ring fade alpha locally once it subscribes to the event.
  void P2_MOTION_LIGHT_FADE_TICKS;
  return out;
}
